import { MasterViewModel } from '../../../base-classes/master-view-model';
import { HttpClient } from '@angular/common/http';
import { signal, computed } from '@angular/core';
import { forkJoin, lastValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import moment from 'moment';
import { MessageHelperService } from '../../../base-classes/master-message-service';
import { ConfirmationService } from 'primeng/api';
import { AuthService } from '../../../auth/login/services/auth.service';
import { ScenarioService } from '../../scenario/services/scenario.service';
import { FuntionalAreaService } from '../../test-suite/services/test-suite.service';
import { ReportGeneratorApiService } from '../services/report-generator-api.service';
import {
    ReportType, ExportFormat, DetailLevel, DateRangeMode, ReportGeneratorConfig,
    ExecutionRunOption, GeneratedReport, ReportPreviewSummary,
    ReportPreviewScenario, ComparisonDelta, ComparisonSummary,
    TrendSummary, FailureAnalysisSummary, StabilityTag, FailureSeverity,
    REPORT_TYPE_OPTIONS, EXPORT_FORMAT_OPTIONS, DETAIL_LEVEL_OPTIONS,
    STATUS_FILTER_OPTIONS, DATE_RANGE_MODE_OPTIONS, ReportTypeOption, SavedReport,
    ComparisonSelectType, COMPARISON_SELECT_TYPE_OPTIONS
} from '../models/report-generator.model';
import { ClientService } from '../../client/services/client.service';
import { PackagesService } from '../../packages/services/packages.service';
import {
    ReportRow, ExecutionGroup, ReportStep
} from '../../report/models/report.model';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { APP_CONFIG } from '../../../config/app.config';

export class ReportGeneratorViewModel extends MasterViewModel<ReportRow> {

    static pendingEditReport: SavedReport | null = null;

    private static _cachedLogoBase64: string | null = null;
    private static readonly LOGO_ASSET_PATH = 'assets/images/logo.png';

    private async getLogoBase64(): Promise<string> {
        if (ReportGeneratorViewModel._cachedLogoBase64) return ReportGeneratorViewModel._cachedLogoBase64;
        try {
            const resp = await fetch(ReportGeneratorViewModel.LOGO_ASSET_PATH);
            const blob = await resp.blob();
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    ReportGeneratorViewModel._cachedLogoBase64 = reader.result as string;
                    resolve(ReportGeneratorViewModel._cachedLogoBase64!);
                };
                reader.readAsDataURL(blob);
            });
        } catch {
            return '';
        }
    }

    private static _cachedTestTypes: any[] | null = null;
    private static _cachedTestTypeMap: Map<string, string> | null = null;
    private static _cachedFunctionalAreas: any[] | null = null;
    private static _cachedFunctionalAreaMap: Map<string, string> | null = null;
    private static _cachedReports: ReportRow[] | null = null;
    private static _cachedReportsLoaded = false;

    // Stepper
    activeStep = signal<number>(1);

    // Step 1: Report Type
    reportTypeOptions = REPORT_TYPE_OPTIONS;
    selectedReportType = signal<ReportType | null>(null);

    // Step 2: Scope / Filters
    executionRuns = signal<ExecutionRunOption[]>([]);
    filteredExecutionRuns = computed<ExecutionRunOption[]>(() => {
        const clientId = this.selectedClient();
        const runs = this.executionRuns();
        if (!clientId) return [];
        if (clientId === 'default') {
            return runs.filter(r => !r.clientId);
        }
        return runs.filter(r => r.clientId === clientId);
    });
    filteredComparisonRuns = computed<ExecutionRunOption[]>(() => {
        const selectedRunId = this.selectedExecutionRunId();
        return this.filteredExecutionRuns().filter(r => r.id !== selectedRunId);
    });
    selectedExecutionRunId = signal<string | null>(null);
    selectedComparisonRunId = signal<string | null>(null);

    // Comparison Select Type (Execution Run vs Tenant Type)
    comparisonSelectTypeOptions = COMPARISON_SELECT_TYPE_OPTIONS;
    selectedComparisonSelectType = signal<ComparisonSelectType>('execution-run');
    tenantOptions = signal<{ label: string; value: string }[]>([]);
    selectedBaselineTenantId = signal<string | null>(null);
    selectedComparisonTenantId = signal<string | null>(null);
    filteredComparisonTenants = computed<{ label: string; value: string }[]>(() => {
        const baselineId = this.selectedBaselineTenantId();
        return this.tenantOptions().filter(t => t.value !== baselineId);
    });

    clientOptions = signal<{ label: string; value: string }[]>([]);
    selectedClient = signal<string | null>(null);
    isClientDropdownDisabled = signal<boolean>(false);

    packageOptions = signal<{ label: string; value: string }[]>([]);
    selectedPackage = signal<string | null>(null);

    testTypeOptions = signal<{ label: string; value: string }[]>([]);
    selectedTestType = signal<string | null>(null);

    functionalAreaOptions = signal<{ label: string; value: string }[]>([]);
    allFunctionalAreaMap = signal<Map<string, string>>(new Map());
    allPackageMap = signal<Map<string, string>>(new Map());
    allTestTypeMap = signal<Map<string, string>>(new Map());
    selectedFunctionalAreas = signal<string[]>([]);

    statusFilterOptions = STATUS_FILTER_OPTIONS;
    selectedStatus = signal<string | null>(null);

    dateRangeModeOptions = DATE_RANGE_MODE_OPTIONS;
    selectedDateRangeMode = signal<DateRangeMode>('fixed');
    dateRange = signal<Date[] | null>(null);
    maxDate = new Date();

    private allFunctionalAreas = signal<{ id: string; name: string; testTypeId: string | null; packageId: string | null; displayLabel: string }[]>([]);
    private allFunctionalAreasRaw = signal<any[]>([]);

    // Step 3: Options
    exportFormatOptions = EXPORT_FORMAT_OPTIONS;
    selectedExportFormat = signal<ExportFormat | null>(null);

    detailLevelOptions = DETAIL_LEVEL_OPTIONS;
    selectedDetailLevel = signal<DetailLevel | null>(null);

    includeScreenshots = signal<boolean>(false);
    includeErrorLogs = signal<boolean>(false);
    includeStepDurations = signal<boolean>(false);

    // Panel state
    panel2Completed = signal<boolean>(false);

    // Preview state
    isGenerating = signal<boolean>(false);
    showPreview = signal<boolean>(false);
    previewSummary = signal<ReportPreviewSummary>({
        totalScenarios: 0, totalSteps: 0, totalPassed: 0,
        totalFailed: 0, totalStopped: 0, passRate: 0
    });
    previewScenarios = signal<ReportPreviewScenario[]>([]);
    previewFailedScenarios = computed(() =>
        this.previewScenarios().filter(s => (s.failed || 0) > 0 || s.status === 'Failed' || s.status === 'Stopped')
    );
    previewFunctionalAreaBreakdown = signal<{ name: string; scenarios: number; totalSteps: number; passed: number; failed: number; stopped: number; passRate: number }[]>([]);
    previewTestTypeBreakdown = signal<{ name: string; scenarios: number; totalSteps: number; passed: number; failed: number; stopped: number; passRate: number }[]>([]);

    // Comparison preview
    comparisonDeltas = signal<ComparisonDelta[]>([]);
    comparisonSummary = signal<ComparisonSummary>({
        nowPassing: 0, newFailures: 0, regressions: 0,
        passRateDelta: 0, beforePassRate: 0, afterPassRate: 0
    });
    baselineScenarios = signal<ReportPreviewScenario[]>([]);
    currentScenarios = signal<ReportPreviewScenario[]>([]);

    // Trend summary
    trendSummary = signal<TrendSummary>({
        runsAnalyzed: 0, avgPassRate: 0, trendDirection: 0, flakyTests: 0, regressions: 0
    });

    // Failure Analysis summary
    failureAnalysisSummary = signal<FailureAnalysisSummary>({
        totalFailed: 0, uniqueFailures: 0, recurring: 0, mostImpactedArea: 'N/A', avgFailureRate: 0
    });

    // Generated reports history
    generatedReports = signal<GeneratedReport[]>([]);

    // Saved reports list
    savedReports = signal<SavedReport[]>([]);
    savedReportsLoading = signal<boolean>(false);
    savedReportsSearchTerm = signal<string>('');
    filteredSavedReports = computed<SavedReport[]>(() => {
        const term = this.savedReportsSearchTerm().toLowerCase().trim();
        const reports = this.savedReports();
        if (!term) return reports;
        return reports.filter(r =>
            r.reportName.toLowerCase().includes(term) ||
            r.reportType.toLowerCase().includes(term) ||
            this.getGeneratedByName(r).toLowerCase().includes(term)
        );
    });

    // Tab / view state
    activeTab = signal<'generate' | 'list'>('generate');
    viewingReport = signal<SavedReport | null>(null);
    editingReportId = signal<string | null>(null);
    isEditMode = computed<boolean>(() => !!this.editingReportId());
    private originalEditSnapshot = signal<string | null>(null);
    hasEditChanges = computed<boolean>(() => {
        if (!this.editingReportId()) return true;
        const original = this.originalEditSnapshot();
        if (!original) return false;
        const current = JSON.stringify(this.buildEditSnapshot());
        return current !== original;
    });
    editingReportName = signal<string>('');
    showSaveDialog = signal<boolean>(false);
    saveReportName = signal<string>('');
    showShareDialog = signal<boolean>(false);
    shareLink = signal<string>('');
    showRenameDialog = signal<boolean>(false);
    renameReportId = signal<string>('');
    renameReportName = signal<string>('');

    // Internal raw data
    private allReports = signal<ReportRow[]>([]);

    // Stream-to-cache signals
    cachedReports = signal<ReportRow[]>([]);
    streamProgress = signal<{ loaded: number; total: number }>({ loaded: 0, total: 0 });
    streamComplete = signal<boolean>(false);
    currentTablePage = signal<number>(0);
    tablePageSize = signal<number>(20);

    // Per-section loading
    summaryLoading = signal<boolean>(false);
    previewLoading = signal<boolean>(false);
    exportLoading = signal<boolean>(false);

    // Computed: display rows from cache
    displayRows = computed<ReportRow[]>(() => {
        const start = this.currentTablePage() * this.tablePageSize();
        return this.cachedReports().slice(start, start + this.tablePageSize());
    });

    totalCachedPages = computed<number>(() => {
        return Math.ceil(this.cachedReports().length / this.tablePageSize());
    });

    private streamSubscription: any = null;

    constructor(
        http: HttpClient,
        private messageService: MessageHelperService,
        private confirmationService: ConfirmationService,
        private authService: AuthService,
        private scenarioService: ScenarioService,
        private functionalAreaService: FuntionalAreaService,
        private reportGeneratorApiService: ReportGeneratorApiService,
        private clientService?: ClientService,
        private packagesService?: PackagesService
    ) {
        super(http, `${environment.apiUrl}/report`, 'Report');
    }

    public init(): void {
        const isGenerateTab = this.activeTab() === 'generate';

        this.loadClients();
        this.loadTenants();
        if (isGenerateTab) {
            this.loadTestTypes();
            this.loadAllFunctionalAreas();
            this.loadReports();
        } else {
            this.loadSavedReports();
        }
    }

    // ===== Data Loading =====

    private loadReports(onComplete?: () => void): void {
        if (ReportGeneratorViewModel._cachedReportsLoaded && ReportGeneratorViewModel._cachedReports) {
            this.allReports.set(ReportGeneratorViewModel._cachedReports);
            this.buildPackageOptions(ReportGeneratorViewModel._cachedReports);
            this.buildExecutionRunsFromReports(ReportGeneratorViewModel._cachedReports);
            onComplete?.();
            return;
        }
        this.getAll().then((list: ReportRow[]) => {
            const reports = list || [];
            ReportGeneratorViewModel._cachedReports = reports;
            ReportGeneratorViewModel._cachedReportsLoaded = true;
            this.allReports.set(reports);
            this.buildPackageOptions(reports);
            this.buildExecutionRunsFromReports(reports);
            onComplete?.();
        }).catch(() => {
            this.messageService.error('Unable to load reports');
            onComplete?.();
        });
    }

    private buildExecutionRunsFromReports(reports: ReportRow[]): void {
        // Group reports by executionId, then compute scenario-status-based pass rate per run
        const runGroups = new Map<string, ReportRow[]>();
        for (const r of reports) {
            const execId = r.executionId;
            if (!execId) continue;
            if (!runGroups.has(execId)) runGroups.set(execId, []);
            runGroups.get(execId)!.push(r);
        }

        const runMap = new Map<string, ExecutionRunOption>();
        const statusOf = (r: ReportRow) => (this.getRowStatus(r) || '').toString().toLowerCase();
        for (const [execId, groupRows] of runGroups.entries()) {
            const passed = groupRows.filter(r => ['passed', 'success'].includes(statusOf(r))).length;
            const failed = groupRows.filter(r => ['failed', 'error'].includes(statusOf(r))).length;
            const validated = passed + failed;
            const passRate = validated > 0 ? Math.round((passed / validated) * 100) : 0;
            const first = groupRows[0];
            runMap.set(execId, {
                id: execId,
                runId: execId.substring(0, 8).toUpperCase(),
                executedOn: first.executedOn || '',
                packageName: first.package?.packageName || 'N/A',
                scenarioCount: groupRows.length,
                passRate,
                executedByName: first.executedByUser
                    ? `${first.executedByUser.firstName || ''} ${first.executedByUser.lastName || ''}`.trim()
                    : 'Unknown',
                clientId: first.clientId || null
            });
        }

        const runs = Array.from(runMap.values())
            .sort((a, b) => new Date(b.executedOn).getTime() - new Date(a.executedOn).getTime());
        this.executionRuns.set(runs);
    }

    private loadTenants(): void {
        if (!this.clientService) return;
        this.clientService.getTenantsAll().subscribe({
            next: (tenants: any[]) => {
                this.tenantOptions.set(
                    (tenants || []).map((t: any) => ({
                        label: t.tenentType || t.description || t.tenantName,
                        value: t.id
                    }))
                );
            },
            error: () => {
                this.tenantOptions.set([]);
            }
        });
    }

    onComparisonSelectTypeChange(type: ComparisonSelectType): void {
        this.selectedComparisonSelectType.set(type);
        this.selectedExecutionRunId.set(null);
        this.selectedComparisonRunId.set(null);
        this.selectedBaselineTenantId.set(null);
        this.selectedComparisonTenantId.set(null);
    }

    isTenantComparisonMode(): boolean {
        return this.isComparisonMode() && this.selectedComparisonSelectType() === 'tenant-type';
    }

    isExecutionRunComparisonMode(): boolean {
        return this.isComparisonMode() && this.selectedComparisonSelectType() === 'execution-run';
    }

    private loadClients(onComplete?: () => void): void {
        this.scenarioService.getClientsAll().subscribe({
            next: (response: any) => {
                const clients = response?.data || response || [];
                this.applyClients(clients);
                onComplete?.();
            },
            error: () => {
                const userInfo = this.authService.getUserInfoFromToken();
                const isSystemUser = userInfo?.isSystemUser === true;
                if (isSystemUser) {
                    this.clientOptions.set([{ label: 'Master Client', value: 'default' }]);
                    this.selectedClient.set('default');
                } else {
                    this.clientOptions.set([]);
                }
                this.isClientDropdownDisabled.set(false);
                onComplete?.();
            }
        });
    }

    private applyClients(clients: any[]): void {
        const opts = clients.map((c: any) => ({ label: c.name || c.clientName, value: c.id }));
        const userInfo = this.authService.getUserInfoFromToken();
        const isSystemUser = userInfo?.isSystemUser === true;

        if (isSystemUser) {
            this.clientOptions.set([{ label: 'Master Client', value: 'default' }, ...opts]);
            this.selectedClient.set('default');
            this.isClientDropdownDisabled.set(false);
            this.loadFunctionalAreasForClient('default');
        } else if (opts.length === 1) {
            this.clientOptions.set(opts);
            this.selectedClient.set(opts[0].value);
            setTimeout(() => this.isClientDropdownDisabled.set(true));
            this.loadFunctionalAreasForClient(opts[0].value);
        } else if (opts.length > 1) {
            this.clientOptions.set(opts);
            this.selectedClient.set(opts[0].value);
            this.isClientDropdownDisabled.set(false);
            this.loadFunctionalAreasForClient(opts[0].value);
        } else {
            this.clientOptions.set(opts);
            this.isClientDropdownDisabled.set(false);
        }
    }

    private loadTestTypes(onComplete?: () => void): void {
        if (ReportGeneratorViewModel._cachedTestTypes && ReportGeneratorViewModel._cachedTestTypeMap) {
            this.testTypeOptions.set(ReportGeneratorViewModel._cachedTestTypes);
            this.allTestTypeMap.set(ReportGeneratorViewModel._cachedTestTypeMap);
            onComplete?.();
            return;
        }
        this.scenarioService.getReferenceList(['TEST_TYPE']).subscribe({
            next: (dataResult: any) => {
                const opts = (dataResult || []).map((item: any) => ({
                    label: item.referenceValue,
                    value: item.id
                }));
                const map = new Map<string, string>();
                for (const item of (dataResult || [])) {
                    map.set(item.id, item.referenceValue);
                }
                ReportGeneratorViewModel._cachedTestTypes = opts;
                ReportGeneratorViewModel._cachedTestTypeMap = map;
                this.testTypeOptions.set(opts);
                this.allTestTypeMap.set(map);
                onComplete?.();
            },
            error: () => {
                this.testTypeOptions.set([]);
                onComplete?.();
            }
        });
    }

    private loadAllFunctionalAreas(onComplete?: () => void): void {
        if (ReportGeneratorViewModel._cachedFunctionalAreas && ReportGeneratorViewModel._cachedFunctionalAreaMap) {
            this.allFunctionalAreasRaw.set(ReportGeneratorViewModel._cachedFunctionalAreas);
            this.allFunctionalAreaMap.set(ReportGeneratorViewModel._cachedFunctionalAreaMap);
            onComplete?.();
            return;
        }
        this.functionalAreaService.getAllFunctionalAreas().subscribe({
            next: (res: any) => {
                const items = res || [];
                const map = new Map<string, string>();
                for (const item of items) {
                    map.set(item.id, item.name);
                }
                ReportGeneratorViewModel._cachedFunctionalAreas = items;
                ReportGeneratorViewModel._cachedFunctionalAreaMap = map;
                this.allFunctionalAreasRaw.set(items);
                this.allFunctionalAreaMap.set(map);
                onComplete?.();
            },
            error: () => {
                onComplete?.();
            }
        });
    }

    private loadFunctionalAreasForClient(clientId: string): void {
        const raw = this.allFunctionalAreasRaw();
        if (raw.length > 0) {
            this.filterFunctionalAreasByClient(raw, clientId);
            return;
        }
        this.functionalAreaService.getAllFunctionalAreas().subscribe({
            next: (res: any) => {
                const items = res || [];
                this.allFunctionalAreasRaw.set(items);
                this.filterFunctionalAreasByClient(items, clientId);
            },
            error: () => {
                this.allFunctionalAreas.set([]);
                this.functionalAreaOptions.set([]);
            }
        });
    }

    private filterFunctionalAreasByClient(items: any[], clientId: string): void {
        const areas = items
            .filter((item: any) => {
                if (clientId === 'default') {
                    return item.isActive && item.clientPackageMappingId === null;
                }
                return item.clientPackageMapping?.client?.id === clientId;
            })
            .map((item: any) => ({
                id: item.id,
                name: item.name,
                testTypeId: item.testType?.id || null,
                packageId: item.package?.id || null,
                displayLabel: item.testType?.referenceValue
                    ? `${item.name} (${item.testType.referenceValue})`
                    : item.name
            }));
        this.allFunctionalAreas.set(areas);
        this.applyTestTypeFiltering();
        this.applyFunctionalAreaFiltering();
    }

    private buildPackageOptions(_reports?: ReportRow[]): void {
        if (!this.packagesService) return;
        this.packagesService.getAllPackages().subscribe({
            next: (packages) => {
                const options = (packages || [])
                    .filter((p: any) => p.isActive !== false)
                    .map((p: any) => ({ label: p.packageName, value: p.id }));
                this.packageOptions.set(options);
                const map = new Map<string, string>();
                for (const p of packages || []) {
                    if (p.id && p.packageName) map.set(p.id, p.packageName);
                }
                this.allPackageMap.set(map);
            },
            error: () => {}
        });
    }

    private applyFunctionalAreaFiltering(): void {
        const selectedTestTypeId = this.selectedTestType();
        const selectedPkgId = this.selectedPackage();
        const allAreas = this.allFunctionalAreas();

        let filtered = allAreas;

        if (selectedPkgId) {
            filtered = filtered.filter(area => area.packageId === selectedPkgId);
        }

        if (selectedTestTypeId) {
            filtered = filtered.filter(area => area.testTypeId === selectedTestTypeId);
        }

        if (filtered.length === 0 && (selectedPkgId || selectedTestTypeId)) {
            filtered = allAreas;
            if (selectedTestTypeId) {
                filtered = filtered.filter(area => area.testTypeId === selectedTestTypeId);
            }
        }

        this.functionalAreaOptions.set(filtered.map(a => ({
            label: a.displayLabel,
            value: a.id
        })));
    }

    private applyTestTypeFiltering(): void {
        if (ReportGeneratorViewModel._cachedTestTypes) {
            this.testTypeOptions.set(ReportGeneratorViewModel._cachedTestTypes);
        }
    }

    onPackageChange(): void {
        this.selectedTestType.set(null);
        this.selectedFunctionalAreas.set([]);
        this.applyTestTypeFiltering();
        if (!this.selectedPackage()) {
            this.functionalAreaOptions.set(this.allFunctionalAreas().map(a => ({
                label: a.displayLabel,
                value: a.id
            })));
        } else {
            this.applyFunctionalAreaFiltering();
        }
    }

    private filterPackagesByClient(clientId: string): void {
        const raw = this.allFunctionalAreasRaw();
        const packageIds = new Set<string>();
        for (const item of raw) {
            if (clientId === 'default') {
                if (item.isActive && item.clientPackageMappingId === null && item.package?.id) {
                    packageIds.add(item.package.id);
                }
            } else {
                if (item.clientPackageMapping?.client?.id === clientId && item.package?.id) {
                    packageIds.add(item.package.id);
                }
            }
        }
        const allMap = this.allPackageMap();
        const filtered = Array.from(packageIds)
            .filter(id => allMap.has(id))
            .map(id => ({ label: allMap.get(id)!, value: id }));
        this.packageOptions.set(filtered);
    }

    // ===== Filter Change Handlers =====

    goToStep(step: number): void {
        this.activeStep.set(step);
    }

    nextStep(): void {
        const current = this.activeStep();
        if (current < 2) this.activeStep.set(current + 1);
    }

    prevStep(): void {
        const current = this.activeStep();
        if (current > 1) this.activeStep.set(current - 1);
    }

    onReportTypeChange(type: ReportType): void {
        this.selectedReportType.set(type);
        this.resetScopeAndOptions();
    }

    private resetScopeAndOptions(): void {
        // Step 2: Scope
        this.selectedExecutionRunId.set(null);
        this.selectedComparisonRunId.set(null);
        this.selectedComparisonSelectType.set('execution-run');
        this.selectedBaselineTenantId.set(null);
        this.selectedComparisonTenantId.set(null);

        const userInfo = this.authService.getUserInfoFromToken();
        const isSystemUser = userInfo?.isSystemUser === true;
        if (isSystemUser) {
            this.selectedClient.set('default');
        } else if (this.clientOptions().length === 1) {
            this.selectedClient.set(this.clientOptions()[0].value);
        } else {
            this.selectedClient.set(null);
        }

        this.selectedPackage.set(null);
        this.selectedTestType.set(null);
        this.selectedFunctionalAreas.set([]);
        this.selectedStatus.set(null);
        this.selectedDateRangeMode.set('fixed');
        this.dateRange.set(null);

        const clientId = this.selectedClient();
        if (clientId) {
            this.filterPackagesByClient(clientId);
            this.loadFunctionalAreasForClient(clientId);
        }

        // Step 3: Options
        this.selectedExportFormat.set(null);
        this.selectedDetailLevel.set(null);
        this.includeScreenshots.set(false);
        this.includeErrorLogs.set(false);
        this.includeStepDurations.set(false);

        // Preview
        this.panel2Completed.set(false);
        this.showPreview.set(false);
        this.previewScenarios.set([]);
        this.previewFunctionalAreaBreakdown.set([]);
        this.previewSummary.set({
            totalScenarios: 0, totalSteps: 0, totalPassed: 0,
            totalFailed: 0, totalStopped: 0, passRate: 0
        });
        this.comparisonDeltas.set([]);
        this.comparisonSummary.set({
            nowPassing: 0, newFailures: 0, regressions: 0,
            passRateDelta: 0, beforePassRate: 0, afterPassRate: 0
        });
    }

    onClientChange(clientId: string): void {
        this.selectedClient.set(clientId);
        this.selectedExecutionRunId.set(null);
        this.selectedComparisonRunId.set(null);
        this.selectedPackage.set(null);
        this.selectedTestType.set(null);
        this.selectedFunctionalAreas.set([]);
        if (clientId) {
            this.filterPackagesByClient(clientId);
            this.loadFunctionalAreasForClient(clientId);
        }
    }

    onTestTypeChange(): void {
        if (!this.selectedTestType()) {
            this.functionalAreaOptions.set(this.allFunctionalAreas().map(a => ({
                label: a.displayLabel,
                value: a.id
            })));
        } else {
            this.applyFunctionalAreaFiltering();
        }
        this.selectedFunctionalAreas.set([]);
    }

    onDateRangeModeChange(mode: DateRangeMode): void {
        this.selectedDateRangeMode.set(mode);
        if (mode !== 'fixed') {
            this.dateRange.set(this.resolveDynamicDateRange(mode));
        } else {
            this.dateRange.set(null);
        }
    }

    resolveDynamicDateRange(mode: DateRangeMode): Date[] {
        const now = new Date();
        const startOfDay = (d: Date) => { d.setHours(0, 0, 0, 0); return d; };
        const end = new Date(now);

        switch (mode) {
            case 'last-7-days':
                return [startOfDay(new Date(now.getTime() - 7 * 86400000)), end];
            case 'last-14-days':
                return [startOfDay(new Date(now.getTime() - 14 * 86400000)), end];
            case 'last-30-days':
                return [startOfDay(new Date(now.getTime() - 30 * 86400000)), end];
            case 'this-week': {
                const day = now.getDay();
                const diff = day === 0 ? 6 : day - 1;
                const monday = new Date(now);
                monday.setDate(now.getDate() - diff);
                return [startOfDay(monday), end];
            }
            case 'this-month':
                return [startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), end];
            case 'this-quarter': {
                const qMonth = Math.floor(now.getMonth() / 3) * 3;
                return [startOfDay(new Date(now.getFullYear(), qMonth, 1)), end];
            }
            default:
                return [startOfDay(new Date(now.getTime() - 7 * 86400000)), end];
        }
    }

    isComparisonMode(): boolean {
        return this.selectedReportType() === 'comparison';
    }

    needsExecutionRun(): boolean {
        const type = this.selectedReportType();
        return type === 'execution-summary' || type === 'comparison';
    }

    isPanel1Valid(): boolean {
        if (!this.selectedReportType()) return false;
        const type = this.selectedReportType();
        if (type === 'filtered' || type === 'trend' || type === 'failure-analysis' ||
            type === 'execution-summary' || type === 'comparison') {
            if (!this.selectedClient()) return false;
        }
        if (this.needsExecutionRun()) {
            if (this.isTenantComparisonMode()) {
                if (!this.selectedBaselineTenantId() || !this.selectedComparisonTenantId()) return false;
            } else {
                if (!this.selectedExecutionRunId()) return false;
                if (this.isComparisonMode() && !this.selectedComparisonRunId()) return false;
            }
        }
        return true;
    }

    needsDateRange(): boolean {
        const type = this.selectedReportType();
        return type === 'filtered' || type === 'trend' || type === 'failure-analysis';
    }

    // ===== Execution Run Display Helpers =====

    formatRunLabel(run: ExecutionRunOption): string {
        const date = run.executedOn ? new Date(run.executedOn) : null;
        const dateStr = date
            ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ', ' +
              date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
            : 'N/A';
        return `${run.runId} | ${dateStr} | ${run.packageName} | ${run.scenarioCount} scenarios | ${run.passRate}% passed | ${run.executedByName}`;
    }

    getPassRateColor(rate: number): string {
        if (rate >= 80) return '#059669';
        if (rate >= 50) return '#d97706';
        return '#dc2626';
    }

    // ===== Report Generation =====

    buildConfig(): ReportGeneratorConfig {
        return {
            reportType: this.selectedReportType(),
            clientId: this.selectedClient(),
            executionRunId: this.selectedExecutionRunId(),
            comparisonRunId: this.selectedComparisonRunId(),
            comparisonSelectType: this.isComparisonMode() ? this.selectedComparisonSelectType() : null,
            baselineTenantId: this.selectedBaselineTenantId(),
            comparisonTenantId: this.selectedComparisonTenantId(),
            packageId: this.selectedPackage(),
            functionalAreaIds: this.selectedFunctionalAreas(),
            testTypeId: this.selectedTestType(),
            statusFilter: this.selectedStatus(),
            dateRangeMode: this.selectedDateRangeMode(),
            dateRange: this.dateRange(),
            detailLevel: this.selectedDetailLevel(),
            exportFormat: this.selectedExportFormat(),
            includeScreenshots: this.includeScreenshots(),
            includeErrorLogs: this.includeErrorLogs(),
            includeStepDurations: this.includeStepDurations()
        };
    }

    canGenerate(): boolean {
        const type = this.selectedReportType();
        if (!type) return false;
        if (!this.selectedClient()) return false;
        if (type === 'execution-summary' && !this.selectedExecutionRunId()) return false;
        if (type === 'comparison') {
            if (this.isTenantComparisonMode()) {
                if (!this.selectedBaselineTenantId() || !this.selectedComparisonTenantId()) return false;
            } else {
                if (!this.selectedExecutionRunId() || !this.selectedComparisonRunId()) return false;
            }
        }
        return true;
    }

    async generatePreview(): Promise<void> {
        if (!this.canGenerate()) {
            this.messageService.error('Please fill required fields');
            return;
        }

        this.panel2Completed.set(true);
        this.isGenerating.set(true);
        this.summaryLoading.set(true);
        this.previewLoading.set(true);

        this.cachedReports.set([]);
        this.streamProgress.set({ loaded: 0, total: 0 });
        this.streamComplete.set(false);
        this.currentTablePage.set(0);

        const filters = this.buildStreamFilters();

        if (this.isComparisonMode()) {
            if (this.isTenantComparisonMode()) {
                this.buildTenantComparisonPreview();
            } else {
                this.buildComparisonPreview();
            }
            this.showPreview.set(true);
        }

        // Fetch summary in parallel (server-side filtering)
        // For comparison mode, the stream-based rebuild will set the correct summary
        if (!this.isComparisonMode()) {
            this.reportGeneratorApiService.getFilteredSummary(filters).subscribe({
                next: (res: any) => {
                    const data = res?.data || res;
                    this.previewSummary.set({
                        totalScenarios: data.totalScenarios || 0,
                        totalSteps: data.totalSteps || 0,
                        totalPassed: data.totalPassed || 0,
                        totalFailed: data.totalFailed || 0,
                        totalStopped: data.totalStopped || 0,
                        passRate: data.passRate || 0
                    });
                    this.summaryLoading.set(false);
                },
                error: () => {
                    this.summaryLoading.set(false);
                    this.messageService.error('Failed to load summary');
                }
            });
        } else {
            this.summaryLoading.set(false);
        }

        // Stream reports (server-side filtering)
        if (this.streamSubscription) {
            this.streamSubscription.unsubscribe();
        }

        this.streamSubscription = this.reportGeneratorApiService.streamReports(filters).subscribe({
            next: (line: string) => {
                try {
                    const batch = JSON.parse(line);
                    const currentCache = this.cachedReports();
                    const newRows = [...currentCache, ...(batch.rows || [])];
                    this.cachedReports.set(newRows);
                    this.streamProgress.set({ loaded: batch.loaded || newRows.length, total: batch.total || 0 });

                    if (this.previewLoading()) {
                        this.previewLoading.set(false);
                        this.buildPreviewFromCache();
                        this.showPreview.set(true);
                        this.isGenerating.set(false);
                    }

                    if (batch.done) {
                        this.streamComplete.set(true);
                        this.buildPreviewFromCache();
                    }
                } catch (e) {
                    console.error('Failed to parse stream batch:', e);
                }
            },
            error: (err: any) => {
                console.error('Stream error:', err);
                this.previewLoading.set(false);
                this.isGenerating.set(false);
                this.messageService.error('Failed to stream reports');
                this.generatePreviewFallback();
            },
            complete: () => {
                this.streamComplete.set(true);
                this.buildPreviewFromCache();
            }
        });
    }

    private buildStreamFilters(): any {
        const isTrendReport = this.selectedReportType() === 'trend';
        const filters: any = {
            clientId: this.selectedClient(),
            packageId: this.selectedPackage(),
            testTypeId: this.selectedTestType(),
            functionalAreaIds: this.selectedFunctionalAreas(),
            // Trend reports analyze all runs over time — don't pin to a single execution
            executionId: isTrendReport ? null : this.selectedExecutionRunId(),
            startDate: this.dateRange()?.[0] ? moment(this.dateRange()![0]).format('YYYY-MM-DD') : undefined,
            endDate: this.dateRange()?.[1] ? moment(this.dateRange()![1]).format('YYYY-MM-DD') : undefined,
            status: this.selectedStatus(),
            batchSize: 50
        };

        if (this.isTenantComparisonMode()) {
            filters.executionId = null;
            const tenantIds = [
                this.selectedBaselineTenantId(),
                this.selectedComparisonTenantId()
            ].filter(Boolean);
            if (tenantIds.length > 0) {
                filters.tenantIds = tenantIds;
            }
        } else if (this.isExecutionRunComparisonMode()) {
            const executionIds = [
                this.selectedExecutionRunId(),
                this.selectedComparisonRunId()
            ].filter(Boolean);
            if (executionIds.length > 1) {
                filters.executionIds = executionIds;
                filters.executionId = null;
            }
        }

        return filters;
    }

    private buildPreviewFromCache(): void {
        const reports = this.cachedReports();

        if (this.isComparisonMode()) {
            if (this.isTenantComparisonMode()) {
                this.buildTenantComparisonFromCached(reports);
            } else {
                this.buildComparisonFromCached(reports);
            }
        } else {
            this.buildStandardPreview(reports);
        }

        if (this.includeScreenshots() || this.selectedDetailLevel() === 'step') {
            this.loadStepsForScenarios(reports);
        }
    }

    private buildTenantComparisonFromCached(reports: ReportRow[]): void {
        const baselineTenantId = this.selectedBaselineTenantId();
        const comparisonTenantId = this.selectedComparisonTenantId();
        if (!baselineTenantId || !comparisonTenantId) {
            this.buildStandardPreview(reports);
            return;
        }

        const baselineReports = reports.filter(r => r.clientTenant?.tenantId === baselineTenantId);
        const comparisonReports = reports.filter(r => r.clientTenant?.tenantId === comparisonTenantId);

        const baselineMap = new Map<string, ReportRow>();
        for (const r of baselineReports) {
            if (r.scenarioId) baselineMap.set(r.scenarioId, r);
        }

        const currentMap = new Map<string, ReportRow>();
        for (const r of comparisonReports) {
            if (r.scenarioId) currentMap.set(r.scenarioId, r);
        }

        const deltas: ComparisonDelta[] = [];
        const allScenarioIds = new Set([...baselineMap.keys(), ...currentMap.keys()]);
        let nowPassing = 0, newFailures = 0, regressions = 0;

        for (const sid of allScenarioIds) {
            const before = baselineMap.get(sid);
            const after = currentMap.get(sid);
            const beforeStatus = before ? this.getRowStatus(before) : 'N/A';
            const afterStatus = after ? this.getRowStatus(after) : 'N/A';

            let changeType: ComparisonDelta['changeType'];
            let details = '';

            if (beforeStatus === 'Failed' && afterStatus === 'Passed') {
                changeType = 'fixed'; nowPassing++;
                details = 'Previously failing, now passing';
            } else if (beforeStatus === 'Passed' && afterStatus === 'Failed') {
                changeType = 'regression'; regressions++;
                details = 'Was passing, now failing';
            } else if (beforeStatus === 'N/A' && afterStatus === 'Failed') {
                changeType = 'new-fail'; newFailures++;
                details = 'New scenario, failing';
            } else if (beforeStatus === 'N/A' && afterStatus === 'Passed') {
                changeType = 'fixed'; nowPassing++;
                details = 'New scenario, passing';
            } else if (afterStatus === 'N/A') {
                changeType = 'stable-fail';
                details = beforeStatus === 'Failed' ? 'Still failing (not re-run)' : 'Not re-run in current';
            } else if (afterStatus === 'Failed') {
                changeType = 'stable-fail';
                details = 'Still failing';
            } else {
                changeType = 'stable-pass';
                details = 'Still passing';
            }

            const scenario = after || before;
            deltas.push({
                scenarioId: sid,
                scenarioName: scenario?.scenario?.scenarioName || 'Unknown',
                functionalAreaName: scenario?.functionalArea?.name || 'Unknown',
                changeType, beforeStatus, afterStatus, details
            });
        }

        this.comparisonDeltas.set(deltas);

        // Count by SCENARIO status (not step counts) — stopped scenarios excluded from denominator
        const statusOf = (r: ReportRow) => (this.getRowStatus(r) || '').toString().toLowerCase();
        const bPassed = baselineReports.filter(r => ['passed', 'success'].includes(statusOf(r))).length;
        const bFailed = baselineReports.filter(r => ['failed', 'error'].includes(statusOf(r))).length;
        const bTotal = bPassed + bFailed;
        const beforePassRate = bTotal > 0 ? Math.round((bPassed / bTotal) * 100) : 0;

        const cPassed = comparisonReports.filter(r => ['passed', 'success'].includes(statusOf(r))).length;
        const cFailed = comparisonReports.filter(r => ['failed', 'error'].includes(statusOf(r))).length;
        const cTotal = cPassed + cFailed;
        const afterPassRate = cTotal > 0 ? Math.round((cPassed / cTotal) * 100) : 0;

        this.comparisonSummary.set({
            nowPassing, newFailures, regressions,
            passRateDelta: afterPassRate - beforePassRate,
            beforePassRate, afterPassRate
        });

        this.baselineScenarios.set(baselineReports.map(r => ({
            scenarioId: r.scenarioId || r.id,
            scenarioName: r.scenario?.scenarioName || 'Unknown',
            functionalAreaName: r.functionalArea?.name || 'Unknown',
            totalSteps: r.numberOfSteps || 0,
            passed: Number(r.passed) || 0,
            failed: Number(r.failed) || 0,
            duration: this.formatDuration(r),
            status: this.getRowStatus(r)
        })));

        this.currentScenarios.set(comparisonReports.map(r => ({
            scenarioId: r.scenarioId || r.id,
            scenarioName: r.scenario?.scenarioName || 'Unknown',
            functionalAreaName: r.functionalArea?.name || 'Unknown',
            totalSteps: r.numberOfSteps || 0,
            passed: Number(r.passed) || 0,
            failed: Number(r.failed) || 0,
            duration: this.formatDuration(r),
            status: this.getRowStatus(r)
        })));

        this.buildStandardPreview(comparisonReports);
    }

    private buildComparisonFromCached(reports: ReportRow[]): void {
        const executionRunId = this.selectedExecutionRunId();
        const comparisonRunId = this.selectedComparisonRunId();
        if (!executionRunId || !comparisonRunId) {
            this.buildStandardPreview(reports);
            return;
        }

        const baselineReports = reports.filter(r => r.executionId === executionRunId);
        const currentReports = reports.filter(r => r.executionId === comparisonRunId);

        const baselineMap = new Map<string, ReportRow>();
        for (const r of baselineReports) {
            if (r.scenarioId) baselineMap.set(r.scenarioId, r);
        }

        const currentMap = new Map<string, ReportRow>();
        for (const r of currentReports) {
            if (r.scenarioId) currentMap.set(r.scenarioId, r);
        }

        const deltas: ComparisonDelta[] = [];
        const allScenarioIds = new Set([...baselineMap.keys(), ...currentMap.keys()]);
        let nowPassing = 0, newFailures = 0, regressions = 0;

        for (const sid of allScenarioIds) {
            const before = baselineMap.get(sid);
            const after = currentMap.get(sid);
            const beforeStatus = before ? this.getRowStatus(before) : 'N/A';
            const afterStatus = after ? this.getRowStatus(after) : 'N/A';

            let changeType: ComparisonDelta['changeType'];
            let details = '';

            if (beforeStatus === 'Failed' && afterStatus === 'Passed') {
                changeType = 'fixed'; nowPassing++;
                details = 'Previously failing, now passing';
            } else if (beforeStatus === 'Passed' && afterStatus === 'Failed') {
                changeType = 'regression'; regressions++;
                details = 'Was passing, now failing';
            } else if (beforeStatus === 'N/A' && afterStatus === 'Failed') {
                changeType = 'new-fail'; newFailures++;
                details = 'New scenario, failing';
            } else if (beforeStatus === 'N/A' && afterStatus === 'Passed') {
                changeType = 'fixed'; nowPassing++;
                details = 'New scenario, passing';
            } else if (afterStatus === 'N/A') {
                changeType = 'stable-fail';
                details = beforeStatus === 'Failed' ? 'Still failing (not re-run)' : 'Not re-run in current';
            } else if (afterStatus === 'Failed') {
                changeType = 'stable-fail';
                details = 'Still failing';
            } else {
                changeType = 'stable-pass';
                details = 'Still passing';
            }

            const scenario = after || before;
            deltas.push({
                scenarioId: sid,
                scenarioName: scenario?.scenario?.scenarioName || 'Unknown',
                functionalAreaName: scenario?.functionalArea?.name || 'Unknown',
                changeType, beforeStatus, afterStatus, details
            });
        }

        this.comparisonDeltas.set(deltas);

        // Count by SCENARIO status (not step counts) — stopped scenarios excluded from denominator
        const statusOf = (r: ReportRow) => (this.getRowStatus(r) || '').toString().toLowerCase();
        const bPassed = baselineReports.filter(r => ['passed', 'success'].includes(statusOf(r))).length;
        const bFailed = baselineReports.filter(r => ['failed', 'error'].includes(statusOf(r))).length;
        const bTotal = bPassed + bFailed;
        const beforePassRate = bTotal > 0 ? Math.round((bPassed / bTotal) * 100) : 0;

        const cPassed = currentReports.filter(r => ['passed', 'success'].includes(statusOf(r))).length;
        const cFailed = currentReports.filter(r => ['failed', 'error'].includes(statusOf(r))).length;
        const cTotal = cPassed + cFailed;
        const afterPassRate = cTotal > 0 ? Math.round((cPassed / cTotal) * 100) : 0;

        this.comparisonSummary.set({
            nowPassing, newFailures, regressions,
            passRateDelta: afterPassRate - beforePassRate,
            beforePassRate, afterPassRate
        });

        this.baselineScenarios.set(baselineReports.map(r => ({
            scenarioId: r.scenarioId || r.id,
            scenarioName: r.scenario?.scenarioName || 'Unknown',
            functionalAreaName: r.functionalArea?.name || 'Unknown',
            totalSteps: r.numberOfSteps || 0,
            passed: Number(r.passed) || 0,
            failed: Number(r.failed) || 0,
            duration: this.formatDuration(r),
            status: this.getRowStatus(r)
        })));

        this.currentScenarios.set(currentReports.map(r => ({
            scenarioId: r.scenarioId || r.id,
            scenarioName: r.scenario?.scenarioName || 'Unknown',
            functionalAreaName: r.functionalArea?.name || 'Unknown',
            totalSteps: r.numberOfSteps || 0,
            passed: Number(r.passed) || 0,
            failed: Number(r.failed) || 0,
            duration: this.formatDuration(r),
            status: this.getRowStatus(r)
        })));

        this.buildStandardPreview(currentReports);
    }

    private generatePreviewFallback(): void {
        // Original logic using allReports for backwards compatibility
        const reports = this.getFilteredReports();
        this.buildStandardPreview(reports);
        this.showPreview.set(true);
        this.isGenerating.set(false);
        if (this.includeScreenshots() || this.selectedDetailLevel() === 'step') {
            this.loadStepsForScenarios(reports);
        }
    }

    onTablePageChange(page: number): void {
        this.currentTablePage.set(page);
    }

    destroy(): void {
        this.streamSubscription?.unsubscribe();
        this.streamSubscription = null;
        this.cachedReports.set([]);
        this.streamProgress.set({ loaded: 0, total: 0 });
        this.streamComplete.set(false);
        this.previewScenarios.set([]);
        this.previewFunctionalAreaBreakdown.set([]);
        this.previewTestTypeBreakdown.set([]);
        this.previewSummary.set({ totalScenarios: 0, totalSteps: 0, totalPassed: 0, totalFailed: 0, totalStopped: 0, passRate: 0 });
    }

    screenshotsLoading = signal<boolean>(false);

    private async loadStepsForScenarios(reports: ReportRow[]): Promise<void> {
        const scenarios = this.previewScenarios();
        if (scenarios.length === 0) return;

        this.screenshotsLoading.set(true);

        try {
            const reportByScenario = new Map<string, string>();
            for (const r of reports) {
                if (r.scenarioId) reportByScenario.set(r.scenarioId, r.id);
                reportByScenario.set(r.id, r.id);
            }

            const isStepLevel = this.selectedDetailLevel() === 'step';
            const updatedScenarios = [...scenarios];

            const requests = scenarios.map((s, idx) => {
                const reportId = reportByScenario.get(s.scenarioId);
                if (!reportId) return Promise.resolve({ idx, steps: [] as any[] });
                return lastValueFrom(this.reportGeneratorApiService.getStepsForReport(reportId))
                    .then((res: any) => {
                        const steps = Array.isArray(res) ? res : (res?.data || res?.steps || []);
                        return { idx, steps };
                    })
                    .catch(() => ({ idx, steps: [] as any[] }));
            });

            const results = await Promise.all(requests);

            for (const { idx, steps } of results) {
                if (!steps || steps.length === 0) continue;
                const mappedSteps = steps
                    .filter((step: any) => isStepLevel || step.screenshotUrl)
                    .map((step: any) => {
                        // Resolve status: prefer status.referenceValue (matches execution-results screen), fall back to passed flag
                        const statusRef = step.status?.referenceValue || step.status?.value || step.status?.name || (typeof step.status === 'string' ? step.status : '');
                        const statusLower = (statusRef || '').toLowerCase();
                        let resolvedStatus: string;
                        if (statusLower.includes('pass') || statusLower.includes('success')) {
                            resolvedStatus = 'Passed';
                        } else if (statusLower.includes('fail') || statusLower.includes('error')) {
                            resolvedStatus = 'Failed';
                        } else if (statusLower.includes('stop') || statusLower.includes('abort')) {
                            resolvedStatus = 'Stopped';
                        } else if (step.passed === true) {
                            resolvedStatus = 'Passed';
                        } else if (step.passed === false) {
                            resolvedStatus = 'Failed';
                        } else {
                            // Unknown state — leave blank rather than incorrectly mark as Stopped
                            resolvedStatus = statusRef || 'Passed';
                        }
                        return {
                            stepOrder: step.stepOrder || 0,
                            stepDescription: this.maskCredentialDescription(step.stepDescription || ''),
                            status: resolvedStatus,
                            duration: step.duration ? `${step.duration}ms` : 'N/A',
                            error: step.error || null,
                            screenshotUrl: this.buildScreenshotUrl(step.screenshotUrl)
                        };
                    });
                if (mappedSteps.length > 0) {
                    updatedScenarios[idx] = { ...updatedScenarios[idx], steps: mappedSteps };
                }
            }

            this.previewScenarios.set(updatedScenarios);
        } catch (e) {
            console.error('loadStepsForScenarios error:', e);
        }

        this.screenshotsLoading.set(false);
    }

    private buildScreenshotUrl(s3Key: string | null): string | null {
        if (!s3Key) return null;
        if (s3Key.startsWith('http')) return s3Key;
        return `${APP_CONFIG.FILE_URL}/${s3Key}`;
    }

    private maskCredentialDescription(desc: string): string {
        if (!desc) return desc;
        return desc.replace(
            /\b(Enter (?:the )?(?:password|passcode|pwd)) as .+$/i,
            '$1 as ******'
        );
    }

    private async fetchImageAsBase64(url: string): Promise<string | null> {
        try {
            const proxyUrl = url.replace(APP_CONFIG.FILE_URL, '/cdn-screenshots');
            const response = await fetch(proxyUrl, { mode: 'cors' });
            if (!response.ok) {
                return this.loadImageViaCanvas(url);
            }
            const blob = await response.blob();
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(blob);
            });
        } catch {
            return this.loadImageViaCanvas(url);
        }
    }

    private loadImageViaCanvas(url: string): Promise<string | null> {
        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.naturalWidth;
                    canvas.height = img.naturalHeight;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) { resolve(null); return; }
                    ctx.drawImage(img, 0, 0);
                    resolve(canvas.toDataURL('image/png'));
                } catch {
                    resolve(null);
                }
            };
            img.onerror = () => resolve(null);
            img.src = url;
        });
    }

    private getFilteredReports(): ReportRow[] {
        let filtered = this.allReports();
        const execRunId = this.selectedExecutionRunId();
        const clientId = this.selectedClient();
        const packageId = this.selectedPackage();
        const testTypeId = this.selectedTestType();
        const areaIds = this.selectedFunctionalAreas();
        const status = this.selectedStatus();
        const dates = this.dateRange();
        const isTrendReport = this.selectedReportType() === 'trend';

        // Trend reports analyze multiple runs over time — don't filter to a single execution
        if (execRunId && !isTrendReport) {
            filtered = filtered.filter(r => r.executionId === execRunId);
        }
        if (clientId && clientId !== 'default') {
            filtered = filtered.filter(r => r.clientId === clientId);
        } else if (clientId === 'default') {
            filtered = filtered.filter(r => !r.clientId);
        }
        if (packageId) {
            filtered = filtered.filter(r => r.packageId === packageId);
        }
        if (testTypeId) {
            filtered = filtered.filter(r => {
                const typeId = r.functionalArea?.testType?.id || r.testTypeId;
                return typeId === testTypeId;
            });
        }
        if (areaIds.length > 0) {
            filtered = filtered.filter(r => r.functionalAreaId && areaIds.includes(r.functionalAreaId));
        }
        if (status && status !== 'all') {
            filtered = filtered.filter(r => this.getRowStatus(r) === status);
        }
        if (dates && dates.length === 2 && dates[0]) {
            const from = new Date(dates[0]);
            from.setHours(0, 0, 0, 0);
            filtered = filtered.filter(r => r.executedOn && new Date(r.executedOn) >= from);
            if (dates[1]) {
                const to = new Date(dates[1]);
                to.setHours(23, 59, 59, 999);
                filtered = filtered.filter(r => r.executedOn && new Date(r.executedOn) <= to);
            }
        }

        return filtered;
    }

    private buildStandardPreview(reports: ReportRow[]): void {
        const totalScenarios = reports.length;
        const totalSteps = reports.reduce((sum, r) => sum + (r.numberOfSteps || 0), 0);

        // Count by SCENARIO status (not step counts) — stopped is separate non-passing category
        const statusOf = (r: ReportRow) => (this.getRowStatus(r) || '').toString().toLowerCase();
        const totalPassed = reports.filter(r => ['passed', 'success'].includes(statusOf(r))).length;
        const totalFailed = reports.filter(r => ['failed', 'error'].includes(statusOf(r))).length;
        const totalStopped = reports.filter(r => ['stopped', 'aborted'].includes(statusOf(r))).length;

        // Pass rate excludes stopped scenarios from denominator (industry standard)
        const validatedScenarios = totalPassed + totalFailed;
        const passRate = validatedScenarios > 0
            ? Math.round((totalPassed / validatedScenarios) * 100) : 0;

        this.previewSummary.set({ totalScenarios, totalSteps, totalPassed, totalFailed, totalStopped, passRate });

        const scenarios: ReportPreviewScenario[] = reports.map(r => {
            const steps = r.numberOfSteps || 0;
            const rawPassed = Number(r.passed) || 0;
            const rawFailed = Number(r.failed) || 0;
            const status = this.getRowStatus(r);
            const statusLower = status.toLowerCase();

            // Reconcile step counts with scenario status (DB may have orphan/missing execution_results)
            // If scenario passed → all steps must be passed (DB gaps don't mean failure)
            // If scenario failed → use DB counts (need to know which step failed)
            // If scenario stopped → use DB counts for executed-before-stop, rest are stopped
            let passed = rawPassed;
            let failed = rawFailed;
            if (['passed', 'success'].includes(statusLower)) {
                passed = steps;
                failed = 0;
            }

            const passRate = (passed + failed) > 0 ? Math.round((passed / (passed + failed)) * 100) : 0;
            return {
                scenarioId: r.scenarioId || r.id,
                scenarioName: r.scenario?.scenarioName || 'Unknown',
                functionalAreaName: r.functionalArea?.name || 'Unknown',
                totalSteps: steps,
                passed,
                failed,
                duration: this.formatDuration(r),
                status,
                packageName: r.package?.packageName || 'N/A',
                testTypeName: r.testType?.referenceValue || 'N/A',
                executionId: r.executionId || 'N/A',
                executedOn: r.executedOn || '',
                passRate
            };
        });
        const reportType = this.selectedReportType();
        let finalScenarios = scenarios;
        if (reportType === 'trend') {
            // Dedupe: keep one row per scenario (latest run) — trend shows evolution, not raw rows
            const latestByScenario = new Map<string, ReportPreviewScenario>();
            for (const s of scenarios) {
                const key = s.scenarioId || s.scenarioName;
                const existing = latestByScenario.get(key);
                if (!existing) {
                    latestByScenario.set(key, s);
                } else {
                    const existingTime = new Date(existing.executedOn || 0).getTime();
                    const currentTime = new Date(s.executedOn || 0).getTime();
                    if (currentTime > existingTime) latestByScenario.set(key, s);
                }
            }
            finalScenarios = Array.from(latestByScenario.values());
            this.enrichTrendScenarios(finalScenarios, reports);
        } else if (reportType === 'failure-analysis') {
            // Dedupe: keep one row per scenario (latest run); aggregate counts come from history in enrich step
            const latestByScenario = new Map<string, ReportPreviewScenario>();
            for (const s of scenarios) {
                const key = s.scenarioId || s.scenarioName;
                const existing = latestByScenario.get(key);
                if (!existing) {
                    latestByScenario.set(key, s);
                } else {
                    const existingTime = new Date(existing.executedOn || 0).getTime();
                    const currentTime = new Date(s.executedOn || 0).getTime();
                    if (currentTime > existingTime) latestByScenario.set(key, s);
                }
            }
            finalScenarios = Array.from(latestByScenario.values());
            this.enrichFailureAnalysisScenarios(finalScenarios, reports);
        }

        this.previewScenarios.set(finalScenarios);

        const faMap = new Map<string, { name: string; scenarios: number; totalSteps: number; passed: number; failed: number; stopped: number }>();
        const statusOfFa = (r: ReportRow) => (this.getRowStatus(r) || '').toString().toLowerCase();
        for (const r of reports) {
            const faName = r.functionalArea?.name || 'Unknown';
            if (!faMap.has(faName)) {
                faMap.set(faName, { name: faName, scenarios: 0, totalSteps: 0, passed: 0, failed: 0, stopped: 0 });
            }
            const entry = faMap.get(faName)!;
            entry.scenarios++;
            entry.totalSteps += r.numberOfSteps || 0;
            const s = statusOfFa(r);
            if (['passed', 'success'].includes(s)) entry.passed++;
            else if (['failed', 'error'].includes(s)) entry.failed++;
            else if (['stopped', 'aborted'].includes(s)) entry.stopped++;
        }
        this.previewFunctionalAreaBreakdown.set(
            Array.from(faMap.values()).map(fa => ({
                ...fa,
                passRate: (fa.passed + fa.failed) > 0 ? Math.round((fa.passed / (fa.passed + fa.failed)) * 100) : 0
            }))
        );

        this.buildTestTypeBreakdown(reports);
    }

    private buildTestTypeBreakdown(reports: ReportRow[]): void {
        const ttMap = new Map<string, { name: string; scenarios: number; totalSteps: number; passed: number; failed: number; stopped: number }>();
        const statusOf = (r: ReportRow) => (this.getRowStatus(r) || '').toString().toLowerCase();
        for (const r of reports) {
            const ttName = r.functionalArea?.testType?.referenceValue || r.testType?.referenceValue || 'Unknown';
            if (!ttMap.has(ttName)) {
                ttMap.set(ttName, { name: ttName, scenarios: 0, totalSteps: 0, passed: 0, failed: 0, stopped: 0 });
            }
            const entry = ttMap.get(ttName)!;
            entry.scenarios++;
            entry.totalSteps += r.numberOfSteps || 0;
            const s = statusOf(r);
            if (['passed', 'success'].includes(s)) entry.passed++;
            else if (['failed', 'error'].includes(s)) entry.failed++;
            else if (['stopped', 'aborted'].includes(s)) entry.stopped++;
        }
        this.previewTestTypeBreakdown.set(
            Array.from(ttMap.values()).map(tt => ({
                ...tt,
                passRate: (tt.passed + tt.failed) > 0 ? Math.round((tt.passed / (tt.passed + tt.failed)) * 100) : 0
            }))
        );
    }

    private enrichTrendScenarios(scenarios: ReportPreviewScenario[], reports: ReportRow[]): void {
        // Use the filtered reports passed in (cachedReports from stream), NOT the global unfiltered allReports()
        // The stream already respects all client/date/package/etc filters, so we should build history only from those
        const execRunGroups = new Map<string, ReportRow[]>();
        for (const r of reports) {
            const execId = r.executionId || 'unknown';
            if (!execRunGroups.has(execId)) execRunGroups.set(execId, []);
            execRunGroups.get(execId)!.push(r);
        }

        const runIds = Array.from(execRunGroups.keys());

        const scenarioRunHistory = new Map<string, { passRate: number; executedOn: string }[]>();
        const nameRunHistory = new Map<string, { passRate: number; executedOn: string }[]>();
        for (const [execId, runReports] of execRunGroups) {
            for (const r of runReports) {
                const sid = r.scenarioId || r.id;
                const name = r.scenario?.scenarioName || '';
                const p = Number(r.passed) || 0;
                const f = Number(r.failed) || 0;
                const total = p + f;
                const rate = total > 0 ? Math.round((p / total) * 100) : 0;
                const entry = { passRate: rate, executedOn: r.executedOn || '' };
                if (!scenarioRunHistory.has(sid)) scenarioRunHistory.set(sid, []);
                scenarioRunHistory.get(sid)!.push(entry);
                if (name) {
                    if (!nameRunHistory.has(name)) nameRunHistory.set(name, []);
                    nameRunHistory.get(name)!.push(entry);
                }
            }
        }

        for (const [, history] of scenarioRunHistory) {
            history.sort((a, b) => new Date(b.executedOn).getTime() - new Date(a.executedOn).getTime());
        }
        for (const [, history] of nameRunHistory) {
            history.sort((a, b) => new Date(b.executedOn).getTime() - new Date(a.executedOn).getTime());
        }

        const prevScenarioRates = new Map<string, number>();
        const prevNameRates = new Map<string, number>();
        for (const [sid, history] of scenarioRunHistory) {
            if (history.length >= 2) {
                prevScenarioRates.set(sid, history[1].passRate);
            }
        }
        for (const [name, history] of nameRunHistory) {
            if (history.length >= 2) {
                prevNameRates.set(name, history[1].passRate);
            }
        }

        let flakyCount = 0;
        let regressionCount = 0;
        let totalRate = 0;

        for (const s of scenarios) {
            const prevRate = prevScenarioRates.get(s.scenarioId) ?? prevNameRates.get(s.scenarioName);
            s.prevPassRate = prevRate !== undefined ? prevRate : undefined;
            s.delta = (s.passRate !== undefined && s.prevPassRate !== undefined)
                ? s.passRate - s.prevPassRate : undefined;

            // Check if scenario currently failed or stopped (by status OR by 0% pass rate)
            const statusLower = (s.status || '').toLowerCase();
            const isCurrentlyFailedOrStopped = statusLower === 'failed'
                || statusLower === 'error'
                || statusLower === 'stopped'
                || statusLower === 'aborted'
                || (s.passRate !== undefined && s.passRate === 0);

            if (isCurrentlyFailedOrStopped) {
                // Failed/stopped/0% scenarios cannot be stable — mark as regressing
                s.stabilityTag = 'regressing';
                regressionCount++;
            } else if (s.delta !== undefined) {
                // Only evaluate pass rate delta for passing scenarios
                if (s.delta < -20) {
                    s.stabilityTag = 'regressing';
                    regressionCount++;
                } else if (s.delta > 20) {
                    s.stabilityTag = 'improving';
                } else if (s.prevPassRate === 100 && s.passRate === 0) {
                    s.stabilityTag = 'flaky';
                    flakyCount++;
                } else {
                    s.stabilityTag = 'stable';
                }
            } else {
                // No prev data — only mark stable if currently passing (>0%)
                if (s.prevPassRate === undefined) {
                    s.stabilityTag = 'new';
                } else {
                    s.stabilityTag = 'stable';
                }
            }
            // Treat stopped/failed scenarios as 0% pass rate for avg calc (matches Overall Pass Rate bar)
            const effectiveRate = isCurrentlyFailedOrStopped ? 0 : (s.passRate || 0);
            totalRate += effectiveRate;
        }

        scenarios.sort((a, b) => (a.delta ?? 0) - (b.delta ?? 0));

        const avgRate = scenarios.length > 0 ? Math.round(totalRate / scenarios.length) : 0;
        const prevAvg = prevScenarioRates.size > 0
            ? Math.round(Array.from(prevScenarioRates.values()).reduce((a, b) => a + b, 0) / prevScenarioRates.size)
            : 0;

        this.trendSummary.set({
            runsAnalyzed: runIds.length,
            avgPassRate: avgRate,
            trendDirection: avgRate - prevAvg,
            flakyTests: flakyCount,
            regressions: regressionCount
        });
    }

    private enrichFailureAnalysisScenarios(scenarios: ReportPreviewScenario[], reports: ReportRow[]): void {
        const allReports = this.allReports();
        const clientId = this.selectedClient();
        const packageId = this.selectedPackage();
        const clientFiltered = allReports.filter(r => {
            const clientMatch = clientId === 'default' ? !r.clientId : r.clientId === clientId;
            if (!clientMatch) return false;
            if (packageId && r.packageId !== packageId) return false;
            return true;
        });
        const execRunGroups = new Map<string, ReportRow[]>();
        for (const r of clientFiltered) {
            const execId = r.executionId || 'unknown';
            if (!execRunGroups.has(execId)) execRunGroups.set(execId, []);
            execRunGroups.get(execId)!.push(r);
        }
        const scenarioRunIds = new Map<string, Set<string>>();
        const scenarioHistory = new Map<string, { failCount: number; firstFail: string; lastFail: string; errors: string[] }>();
        for (const [execId, runReports] of execRunGroups) {
            for (const r of runReports) {
                const sid = r.scenarioId || r.id;
                if (!scenarioRunIds.has(sid)) scenarioRunIds.set(sid, new Set());
                scenarioRunIds.get(sid)!.add(execId);

                const status = this.getRowStatus(r);
                if (status === 'Failed' || status === 'Stopped') {
                    if (!scenarioHistory.has(sid)) {
                        scenarioHistory.set(sid, { failCount: 0, firstFail: '', lastFail: '', errors: [] });
                    }
                    const h = scenarioHistory.get(sid)!;
                    h.failCount++;
                    const execDate = r.executedOn || '';
                    if (!h.firstFail || execDate < h.firstFail) h.firstFail = execDate;
                    if (!h.lastFail || execDate > h.lastFail) h.lastFail = execDate;
                }
            }
        }

        const errorCategories = ['Element Not Found', 'Timeout', 'Assertion Failed', 'Navigation Error', 'Unknown'];
        let uniqueFailures = 0;
        let recurringCount = 0;
        const impactedAreas = new Map<string, number>();

        for (const s of scenarios) {
            const history = scenarioHistory.get(s.scenarioId);
            s.totalRunsAnalyzed = scenarioRunIds.get(s.scenarioId)?.size || 0;
            s.failureCount = history?.failCount || (s.status === 'Failed' || s.status === 'Stopped' ? 1 : 0);
            s.firstFailedDate = history?.firstFail || '';
            s.lastFailedDate = history?.lastFail || '';

            const failRatio = s.totalSteps > 0 ? s.failed / s.totalSteps : 0;
            if (failRatio >= 0.8) s.errorCategory = 'Assertion Failed';
            else if (failRatio >= 0.5) s.errorCategory = 'Element Not Found';
            else if (s.status === 'Stopped') s.errorCategory = 'Timeout';
            else if (s.failed > 0) s.errorCategory = 'Navigation Error';
            else s.errorCategory = 'Unknown';

            if (failRatio >= 0.8) s.rootCause = 'Data mismatch';
            else if (s.errorCategory === 'Element Not Found') s.rootCause = 'Selector changed';
            else if (s.errorCategory === 'Timeout') s.rootCause = 'Slow page load';
            else if (s.errorCategory === 'Navigation Error') s.rootCause = 'URL redirect';
            else s.rootCause = 'Expected value changed';

            if (s.failureCount >= 3 && failRatio >= 0.5) s.severity = 'critical';
            else if (s.failureCount >= 2 || failRatio >= 0.3) s.severity = 'high';
            else if (s.failed > 0) s.severity = 'medium';
            else s.severity = 'low';

            if (s.failed > 0 || s.status === 'Failed' || s.status === 'Stopped') {
                uniqueFailures++;
                if (s.failureCount >= 3) recurringCount++;
                const areaCount = impactedAreas.get(s.functionalAreaName) || 0;
                impactedAreas.set(s.functionalAreaName, areaCount + 1);
            }
        }

        const severityOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
        scenarios.sort((a, b) => (severityOrder[a.severity || 'low'] - severityOrder[b.severity || 'low']) || ((b.failureCount || 0) - (a.failureCount || 0)));

        let mostImpacted = 'N/A';
        let maxCount = 0;
        for (const [area, count] of impactedAreas) {
            if (count > maxCount) { mostImpacted = area; maxCount = count; }
        }

        const totalFailed = scenarios.filter(s => s.failed > 0 || s.status === 'Failed' || s.status === 'Stopped').length;
        const avgFailRate = scenarios.length > 0 ? Math.round((totalFailed / scenarios.length) * 100) : 0;

        this.failureAnalysisSummary.set({
            totalFailed,
            uniqueFailures,
            recurring: recurringCount,
            mostImpactedArea: mostImpacted,
            avgFailureRate: avgFailRate
        });
    }

    private recomputeTrendSummaryFromScenarios(scenarios: any[]): void {
        if (!scenarios.length) return;
        const totalRate = scenarios.reduce((sum: number, s: any) => sum + (s.passRate || 0), 0);
        const avgRate = Math.round(totalRate / scenarios.length);
        const flakyCount = scenarios.filter((s: any) => s.stabilityTag === 'flaky').length;
        const regressionCount = scenarios.filter((s: any) => s.stabilityTag === 'regressing').length;

        const deltas = scenarios.filter((s: any) => s.delta !== undefined && s.delta !== null);
        const avgDelta = deltas.length > 0
            ? Math.round(deltas.reduce((sum: number, s: any) => sum + s.delta, 0) / deltas.length)
            : 0;

        const runIds = new Set(scenarios.map((s: any) => s.executionId).filter(Boolean));

        this.trendSummary.set({
            runsAnalyzed: runIds.size || scenarios.length,
            avgPassRate: avgRate,
            trendDirection: avgDelta,
            flakyTests: flakyCount,
            regressions: regressionCount
        });
    }

    private recomputeFailureAnalysisSummaryFromScenarios(scenarios: any[]): void {
        if (!scenarios.length) return;
        let uniqueFailures = 0;
        let recurringCount = 0;
        const areaCounts = new Map<string, number>();

        for (const s of scenarios) {
            if (s.failed > 0 || s.status === 'Failed' || s.status === 'Stopped') {
                uniqueFailures++;
                if ((s.failureCount || 0) >= 3) recurringCount++;
                const area = s.functionalAreaName || 'Unknown';
                areaCounts.set(area, (areaCounts.get(area) || 0) + 1);
            }
        }

        const totalFailed = uniqueFailures;
        const avgFailRate = scenarios.length > 0 ? Math.round((totalFailed / scenarios.length) * 100) : 0;

        let mostImpacted = 'N/A';
        let maxCount = 0;
        for (const [area, count] of areaCounts) {
            if (count > maxCount) { mostImpacted = area; maxCount = count; }
        }

        this.failureAnalysisSummary.set({
            totalFailed,
            uniqueFailures,
            recurring: recurringCount,
            mostImpactedArea: mostImpacted,
            avgFailureRate: avgFailRate
        });
    }

    getStabilityColor(tag: StabilityTag | undefined): string {
        switch (tag) {
            case 'regressing': return '#dc2626';
            case 'flaky': return '#d97706';
            case 'improving': return '#059669';
            case 'stable': return '#059669';
            case 'new': return '#6b7280';
            default: return '#6b7280';
        }
    }

    getStabilityLabel(tag: StabilityTag | undefined): string {
        switch (tag) {
            case 'regressing': return 'Regressing';
            case 'flaky': return 'Flaky';
            case 'improving': return 'Improving';
            case 'stable': return 'Stable';
            case 'new': return 'New';
            default: return '—';
        }
    }

    getDeltaColor(delta: number | undefined): string {
        if (delta === undefined) return '#6b7280';
        if (delta > 0) return '#059669';
        if (delta < 0) return '#dc2626';
        return '#6b7280';
    }

    formatDelta(delta: number | undefined): string {
        if (delta === undefined) return '—';
        if (delta > 0) return `+${delta}%`;
        if (delta < 0) return `${delta}%`;
        return '0%';
    }

    getSeverityClass(severity: FailureSeverity | undefined): string {
        switch (severity) {
            case 'critical': return 'bg-red-100 text-red-700 font-bold';
            case 'high': return 'bg-amber-100 text-amber-700 font-bold';
            case 'medium': return 'bg-sky-100 text-sky-700 font-bold';
            case 'low': return 'bg-surface-100 text-surface-600';
            default: return 'text-surface-500';
        }
    }

    getSeverityLabel(severity: FailureSeverity | undefined): string {
        return severity ? severity.charAt(0).toUpperCase() + severity.slice(1) : '—';
    }

    private buildComparisonPreview(): void {
        const baselineRunId = this.selectedExecutionRunId();
        const currentRunId = this.selectedComparisonRunId();
        if (!baselineRunId || !currentRunId) return;

        const allReports = this.allReports();
        const baselineReports = allReports.filter(r => r.executionId === baselineRunId);
        const currentReports = allReports.filter(r => r.executionId === currentRunId);

        const baselineMap = new Map<string, ReportRow>();
        for (const r of baselineReports) {
            if (r.scenarioId) baselineMap.set(r.scenarioId, r);
        }

        const currentMap = new Map<string, ReportRow>();
        for (const r of currentReports) {
            if (r.scenarioId) currentMap.set(r.scenarioId, r);
        }

        const deltas: ComparisonDelta[] = [];
        const allScenarioIds = new Set([...baselineMap.keys(), ...currentMap.keys()]);

        let nowPassing = 0, newFailures = 0, regressions = 0;

        for (const sid of allScenarioIds) {
            const before = baselineMap.get(sid);
            const after = currentMap.get(sid);
            const beforeStatus = before ? this.getRowStatus(before) : 'N/A';
            const afterStatus = after ? this.getRowStatus(after) : 'N/A';

            let changeType: ComparisonDelta['changeType'];
            let details = '';

            if (beforeStatus === 'Failed' && afterStatus === 'Passed') {
                changeType = 'fixed';
                nowPassing++;
                details = 'Previously failing, now passing';
            } else if (beforeStatus === 'Passed' && afterStatus === 'Failed') {
                changeType = 'regression';
                regressions++;
                details = 'Was passing, now failing';
            } else if (beforeStatus === 'N/A' && afterStatus === 'Failed') {
                changeType = 'new-fail';
                newFailures++;
                details = 'New scenario, failing';
            } else if (afterStatus === 'Failed') {
                changeType = 'stable-fail';
                details = 'Still failing';
            } else {
                changeType = 'stable-pass';
                details = 'Still passing';
            }

            const scenario = after || before;
            deltas.push({
                scenarioId: sid,
                scenarioName: scenario?.scenario?.scenarioName || 'Unknown',
                functionalAreaName: scenario?.functionalArea?.name || 'Unknown',
                changeType,
                beforeStatus,
                afterStatus,
                details
            });
        }

        this.comparisonDeltas.set(deltas);

        // Count by SCENARIO status (not step counts) — stopped scenarios excluded from denominator
        const statusOf = (r: ReportRow) => (this.getRowStatus(r) || '').toString().toLowerCase();
        const bPassed = baselineReports.filter(r => ['passed', 'success'].includes(statusOf(r))).length;
        const bFailed = baselineReports.filter(r => ['failed', 'error'].includes(statusOf(r))).length;
        const bTotal = bPassed + bFailed;
        const beforePassRate = bTotal > 0 ? Math.round((bPassed / bTotal) * 100) : 0;

        const cPassed = currentReports.filter(r => ['passed', 'success'].includes(statusOf(r))).length;
        const cFailed = currentReports.filter(r => ['failed', 'error'].includes(statusOf(r))).length;
        const cTotal = cPassed + cFailed;
        const afterPassRate = cTotal > 0 ? Math.round((cPassed / cTotal) * 100) : 0;

        this.comparisonSummary.set({
            nowPassing,
            newFailures,
            regressions,
            passRateDelta: afterPassRate - beforePassRate,
            beforePassRate,
            afterPassRate
        });

        this.baselineScenarios.set(baselineReports.map(r => ({
            scenarioId: r.scenarioId || r.id,
            scenarioName: r.scenario?.scenarioName || 'Unknown',
            functionalAreaName: r.functionalArea?.name || 'Unknown',
            totalSteps: r.numberOfSteps || 0,
            passed: Number(r.passed) || 0,
            failed: Number(r.failed) || 0,
            duration: this.formatDuration(r),
            status: this.getRowStatus(r)
        })));

        this.currentScenarios.set(currentReports.map(r => ({
            scenarioId: r.scenarioId || r.id,
            scenarioName: r.scenario?.scenarioName || 'Unknown',
            functionalAreaName: r.functionalArea?.name || 'Unknown',
            totalSteps: r.numberOfSteps || 0,
            passed: Number(r.passed) || 0,
            failed: Number(r.failed) || 0,
            duration: this.formatDuration(r),
            status: this.getRowStatus(r)
        })));

        this.buildStandardPreview(currentReports);
    }

    private buildTenantComparisonPreview(): void {
        const baselineTenantId = this.selectedBaselineTenantId();
        const comparisonTenantId = this.selectedComparisonTenantId();
        if (!baselineTenantId || !comparisonTenantId) return;

        const clientId = this.selectedClient();
        const allReports = this.allReports();

        const clientFiltered = allReports.filter(r => {
            if (clientId === 'default') return !r.clientId;
            return r.clientId === clientId;
        });

        const baselineReports = clientFiltered.filter(r => r.tenantId === baselineTenantId || r.clientTenant?.tenantId === baselineTenantId);
        const comparisonReports = clientFiltered.filter(r => r.tenantId === comparisonTenantId || r.clientTenant?.tenantId === comparisonTenantId);

        const baselineMap = new Map<string, ReportRow>();
        for (const r of baselineReports) {
            if (r.scenarioId) baselineMap.set(r.scenarioId, r);
        }

        const currentMap = new Map<string, ReportRow>();
        for (const r of comparisonReports) {
            if (r.scenarioId) currentMap.set(r.scenarioId, r);
        }

        const deltas: ComparisonDelta[] = [];
        const allScenarioIds = new Set([...baselineMap.keys(), ...currentMap.keys()]);

        let nowPassing = 0, newFailures = 0, regressions = 0;

        for (const sid of allScenarioIds) {
            const before = baselineMap.get(sid);
            const after = currentMap.get(sid);
            const beforeStatus = before ? this.getRowStatus(before) : 'N/A';
            const afterStatus = after ? this.getRowStatus(after) : 'N/A';

            let changeType: ComparisonDelta['changeType'];
            let details = '';

            if (beforeStatus === 'Failed' && afterStatus === 'Passed') {
                changeType = 'fixed';
                nowPassing++;
                details = 'Previously failing, now passing';
            } else if (beforeStatus === 'Passed' && afterStatus === 'Failed') {
                changeType = 'regression';
                regressions++;
                details = 'Was passing, now failing';
            } else if (beforeStatus === 'N/A' && afterStatus === 'Failed') {
                changeType = 'new-fail';
                newFailures++;
                details = 'New scenario, failing';
            } else if (afterStatus === 'Failed') {
                changeType = 'stable-fail';
                details = 'Still failing';
            } else {
                changeType = 'stable-pass';
                details = 'Still passing';
            }

            const scenario = after || before;
            deltas.push({
                scenarioId: sid,
                scenarioName: scenario?.scenario?.scenarioName || 'Unknown',
                functionalAreaName: scenario?.functionalArea?.name || 'Unknown',
                changeType,
                beforeStatus,
                afterStatus,
                details
            });
        }

        this.comparisonDeltas.set(deltas);

        // Count by SCENARIO status (not step counts) — stopped scenarios excluded from denominator
        const statusOf = (r: ReportRow) => (this.getRowStatus(r) || '').toString().toLowerCase();
        const bPassed = baselineReports.filter(r => ['passed', 'success'].includes(statusOf(r))).length;
        const bFailed = baselineReports.filter(r => ['failed', 'error'].includes(statusOf(r))).length;
        const bTotal = bPassed + bFailed;
        const beforePassRate = bTotal > 0 ? Math.round((bPassed / bTotal) * 100) : 0;

        const cPassed = comparisonReports.filter(r => ['passed', 'success'].includes(statusOf(r))).length;
        const cFailed = comparisonReports.filter(r => ['failed', 'error'].includes(statusOf(r))).length;
        const cTotal = cPassed + cFailed;
        const afterPassRate = cTotal > 0 ? Math.round((cPassed / cTotal) * 100) : 0;

        this.comparisonSummary.set({
            nowPassing,
            newFailures,
            regressions,
            passRateDelta: afterPassRate - beforePassRate,
            beforePassRate,
            afterPassRate
        });

        this.baselineScenarios.set(baselineReports.map(r => ({
            scenarioId: r.scenarioId || r.id,
            scenarioName: r.scenario?.scenarioName || 'Unknown',
            functionalAreaName: r.functionalArea?.name || 'Unknown',
            totalSteps: r.numberOfSteps || 0,
            passed: Number(r.passed) || 0,
            failed: Number(r.failed) || 0,
            duration: this.formatDuration(r),
            status: this.getRowStatus(r)
        })));

        this.currentScenarios.set(comparisonReports.map(r => ({
            scenarioId: r.scenarioId || r.id,
            scenarioName: r.scenario?.scenarioName || 'Unknown',
            functionalAreaName: r.functionalArea?.name || 'Unknown',
            totalSteps: r.numberOfSteps || 0,
            passed: Number(r.passed) || 0,
            failed: Number(r.failed) || 0,
            duration: this.formatDuration(r),
            status: this.getRowStatus(r)
        })));

        this.buildStandardPreview(comparisonReports);
    }

    private rebuildComparisonFromScenarios(report: SavedReport): void {
        const scenarios = report.previewScenarios || [];
        const summary = report.previewSummary;

        const passRate = summary?.passRate || 0;
        const passed = scenarios.filter(s => s.status === 'Passed').length;
        const failed = scenarios.filter(s => s.status === 'Failed').length;

        this.currentScenarios.set(scenarios);
        this.baselineScenarios.set(scenarios);

        const deltas: ComparisonDelta[] = scenarios.map(s => ({
            scenarioId: s.scenarioId,
            scenarioName: s.scenarioName,
            functionalAreaName: s.functionalAreaName,
            changeType: s.status === 'Passed' ? 'stable-pass' as const : 'stable-fail' as const,
            beforeStatus: s.status,
            afterStatus: s.status,
            details: s.status === 'Passed' ? 'Still passing' : (s.status === 'Failed' ? 'Still failing' : 'Not run')
        }));
        this.comparisonDeltas.set(deltas);

        this.comparisonSummary.set({
            beforePassRate: passRate,
            afterPassRate: passRate,
            passRateDelta: 0,
            nowPassing: 0,
            regressions: 0,
            newFailures: 0
        });
    }

    // ===== Export: PDF =====

    private pdfSectionTitle(doc: jsPDF, num: number, title: string, y: number): number {
        const pw = doc.internal.pageSize.getWidth();
        doc.setFillColor(26, 86, 219);
        doc.circle(20, y - 3, 4, 'F');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text(String(num), 20, y - 1.5, { align: 'center' });
        doc.setFontSize(13);
        doc.setTextColor(30, 41, 59);
        doc.text(title, 28, y);
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.4);
        doc.line(14, y + 3, pw - 14, y + 3);
        return y + 10;
    }

    private pdfKpiCard(doc: jsPDF, x: number, y: number, w: number, value: string, label: string,
        bgColor: [number, number, number], textColor: [number, number, number], borderColor: [number, number, number]): void {
        doc.setDrawColor(...borderColor);
        doc.setFillColor(...bgColor);
        doc.roundedRect(x, y, w, 22, 2, 2, 'FD');
        let fontSize = 18;
        if (value.length > 8) fontSize = 11;
        else if (value.length > 5) fontSize = 14;
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...textColor);
        doc.text(value, x + w / 2, y + 11, { align: 'center', maxWidth: w - 4 });
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(100, 116, 139);
        doc.text(label.toUpperCase(), x + w / 2, y + 18, { align: 'center' });
    }

    private pdfTableStyles(): any {
        return {
            headStyles: {
                fillColor: [248, 250, 252],
                textColor: [100, 116, 139],
                fontStyle: 'bold',
                fontSize: 8,
                cellPadding: 4
            },
            bodyStyles: {
                fontSize: 9,
                cellPadding: 4,
                textColor: [30, 41, 59]
            },
            alternateRowStyles: { fillColor: [250, 251, 252] },
            margin: { left: 14, right: 14 },
            tableWidth: 'auto'
        };
    }

    async exportPdf(): Promise<void> {
        const wideTypes: (ReportType | null)[] = ['trend', 'filtered', 'execution-summary'];
        const orientation = wideTypes.includes(this.selectedReportType()) ? 'landscape' : 'portrait';
        const doc = new jsPDF({ orientation });
        const pageWidth = doc.internal.pageSize.getWidth();
        const rightMargin = pageWidth - 14;
        const summary = this.previewSummary();
        const scenarios = this.previewScenarios();
        const reportType = this.selectedReportType();

        // Header with logo
        try { const logoData = await this.getLogoBase64(); if (logoData) doc.addImage(logoData, 'PNG', 14, 10, 40, 16); } catch (_) {}

        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 41, 59);
        doc.text('Test Accelerator', 58, 21);

        // Meta grid (space-between)
        const metaY = 34;
        const metaItems = [
            { label: 'Report Type', value: REPORT_TYPE_OPTIONS.find(o => o.id === reportType)?.label || reportType || 'N/A' },
            { label: 'Total Scenarios', value: String(summary.totalScenarios) },
            { label: 'Pass Rate', value: `${summary.passRate}%` },
            { label: 'Generated By', value: this.getCurrentUserName() }
        ];
        const metaSpacing = (rightMargin - 14) / metaItems.length;
        metaItems.forEach((item, i) => {
            const mx = 14 + (i * metaSpacing);
            doc.setFontSize(7);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(148, 163, 184);
            doc.text(item.label.toUpperCase(), mx, metaY);
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(30, 41, 59);
            doc.text(item.value, mx, metaY + 5);
        });

        // Header separator
        doc.setDrawColor(26, 86, 219);
        doc.setLineWidth(0.8);
        doc.line(14, 44, rightMargin, 44);

        // Report Configuration Details
        let configY = 50;
        const configDetails = this.getReportConfigDetails(this.viewingReport());
        if (configDetails.length > 0) {
            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(100, 116, 139);
            doc.text('REPORT CONFIGURATION', 14, configY);
            configY += 5;
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            for (const detail of configDetails) {
                doc.setTextColor(100, 116, 139);
                doc.text(`${detail.label}:`, 14, configY);
                doc.setTextColor(30, 41, 59);
                doc.text(detail.value, 55, configY);
                configY += 4;
            }
            configY += 4;
        }

        // Section 1: KPI cards (type-specific)
        let y = this.pdfSectionTitle(doc, 1, 'Execution Overview', configY);
        const availableW = pageWidth - 28;
        const gap = 3;
        const cardW = (availableW - gap * 4) / 5;
        const startX = 14;

        if (reportType === 'trend') {
            const trend = this.trendSummary();
            this.pdfKpiCard(doc, startX, y, cardW, String(trend.runsAnalyzed), 'Runs Analyzed',
                [232, 238, 251], [26, 86, 219], [226, 232, 240]);
            this.pdfKpiCard(doc, startX + cardW + gap, y, cardW, `${trend.avgPassRate}%`, 'Avg Pass Rate',
                [209, 250, 229], [5, 150, 105], [187, 247, 208]);
            this.pdfKpiCard(doc, startX + (cardW + gap) * 2, y, cardW, `${trend.trendDirection >= 0 ? '+' : ''}${trend.trendDirection}%`, 'Trend Direction',
                trend.trendDirection >= 0 ? [209, 250, 229] : [254, 226, 226], trend.trendDirection >= 0 ? [5, 150, 105] : [220, 38, 38], trend.trendDirection >= 0 ? [187, 247, 208] : [254, 202, 202]);
            this.pdfKpiCard(doc, startX + (cardW + gap) * 3, y, cardW, String(trend.flakyTests), 'Flaky Tests',
                [254, 249, 235], [217, 119, 6], [254, 243, 199]);
            this.pdfKpiCard(doc, startX + (cardW + gap) * 4, y, cardW, String(trend.regressions), 'Regressions',
                [254, 226, 226], [220, 38, 38], [254, 202, 202]);
        } else if (reportType === 'failure-analysis') {
            const fa = this.failureAnalysisSummary();
            this.pdfKpiCard(doc, startX, y, cardW, String(fa.totalFailed), 'Total Failed',
                [254, 226, 226], [220, 38, 38], [254, 202, 202]);
            this.pdfKpiCard(doc, startX + cardW + gap, y, cardW, String(fa.uniqueFailures), 'Unique Failures',
                [254, 249, 235], [217, 119, 6], [254, 243, 199]);
            this.pdfKpiCard(doc, startX + (cardW + gap) * 2, y, cardW, String(fa.recurring), 'Recurring (3+)',
                [254, 226, 226], [220, 38, 38], [254, 202, 202]);
            this.pdfKpiCard(doc, startX + (cardW + gap) * 3, y, cardW, fa.mostImpactedArea, 'Most Impacted',
                [232, 238, 251], [26, 86, 219], [226, 232, 240]);
            this.pdfKpiCard(doc, startX + (cardW + gap) * 4, y, cardW, `${fa.avgFailureRate}%`, 'Avg Failure Rate',
                [254, 226, 226], [220, 38, 38], [254, 202, 202]);
        } else {
            this.pdfKpiCard(doc, startX, y, cardW, String(summary.totalScenarios), 'Scenarios',
                [232, 238, 251], [26, 86, 219], [226, 232, 240]);
            this.pdfKpiCard(doc, startX + cardW + gap, y, cardW, String(summary.totalPassed), 'Passed',
                [209, 250, 229], [5, 150, 105], [187, 247, 208]);
            this.pdfKpiCard(doc, startX + (cardW + gap) * 2, y, cardW, String(summary.totalFailed), 'Failed',
                [254, 226, 226], [220, 38, 38], [254, 202, 202]);
            this.pdfKpiCard(doc, startX + (cardW + gap) * 3, y, cardW, String(summary.totalStopped), 'Stopped',
                [243, 244, 246], [107, 114, 128], [226, 232, 240]);
            this.pdfKpiCard(doc, startX + (cardW + gap) * 4, y, cardW, `${summary.passRate}%`, 'Pass Rate',
                [240, 253, 244], [5, 150, 105], [187, 247, 208]);
        }
        y += 28;

        // Progress bar
        const barY = y;
        const barW = pageWidth - 28;
        const passedW = summary.totalSteps > 0 ? (summary.totalPassed / summary.totalSteps) * barW : 0;
        const failedW = summary.totalSteps > 0 ? (summary.totalFailed / summary.totalSteps) * barW : 0;
        const stoppedW = Math.max(0, barW - passedW - failedW);

        doc.setFillColor(243, 244, 246);
        doc.roundedRect(14, barY, barW, 6, 3, 3, 'F');
        if (passedW > 0) { doc.setFillColor(5, 150, 105); doc.roundedRect(14, barY, passedW, 6, 3, 3, 'F'); }
        if (failedW > 0) { doc.setFillColor(220, 38, 38); doc.rect(14 + passedW, barY, failedW, 6, 'F'); }
        if (stoppedW > 0 && summary.totalStopped > 0) { doc.setFillColor(107, 114, 128); doc.rect(14 + passedW + failedW, barY, stoppedW, 6, 'F'); }

        // Legend
        y = barY + 10;
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        [[5, 150, 105, `Passed (${summary.totalPassed})`], [220, 38, 38, `Failed (${summary.totalFailed})`], [107, 114, 128, `Stopped (${summary.totalStopped})`]]
            .forEach((item: any, i) => {
                const lx = 14 + (i * 50);
                doc.setFillColor(item[0], item[1], item[2]);
                doc.circle(lx + 2, y, 1.5, 'F');
                doc.setTextColor(100, 116, 139);
                doc.text(item[3], lx + 6, y + 1);
            });
        y += 10;

        // Section 2: Comparison or Scenario Results
        if (this.isComparisonMode()) {
            y = this.pdfSectionTitle(doc, 2, 'Execution Comparison', y);
            const compSummary = this.comparisonSummary();
            const baselinePassed = this.getBaselinePassedCount();
            const baselineFailed = this.getBaselineFailedCount();
            const currentPassed = this.getCurrentPassedCount();
            const currentFailed = this.getCurrentFailedCount();
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(30, 41, 59);
            doc.text(`Baseline: ${this.getBaselineRunLabel()}`, 14, y); y += 5;
            doc.text(`Current:  ${this.getComparisonRunLabel()}`, 14, y); y += 5;
            doc.text(`Baseline Pass Rate: ${compSummary.beforePassRate}%  |  Current Pass Rate: ${compSummary.afterPassRate}%  |  Delta: ${compSummary.passRateDelta >= 0 ? '+' : ''}${compSummary.passRateDelta}%`, 14, y); y += 5;
            doc.text(`Now Passing: +${compSummary.nowPassing}  |  New Failures: ${compSummary.newFailures}  |  Regressions: ${compSummary.regressions}`, 14, y); y += 8;

            const deltas = this.comparisonDeltas();
            if (deltas.length > 0) {
                autoTable(doc, {
                    startY: y,
                    head: [['#', 'Scenario', 'Functional Area', 'Before (Baseline)', 'After (Current)', 'Change', 'Details']],
                    body: deltas.map((d, i) => [i + 1, d.scenarioName, d.functionalAreaName, d.beforeStatus, d.afterStatus, this.getChangeTagLabel(d.changeType), d.details]),
                    ...this.pdfTableStyles(),
                    columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 35, fontStyle: 'bold' }, 2: { cellWidth: 25 } },
                    didParseCell: (data: any) => {
                        if (data.section !== 'body') return;
                        if (data.column.index === 3 || data.column.index === 4) {
                            const val = data.cell.raw;
                            data.cell.styles.fontStyle = 'bold';
                            if (val === 'Passed') data.cell.styles.textColor = [5, 150, 105];
                            else if (val === 'Failed') data.cell.styles.textColor = [220, 38, 38];
                            else data.cell.styles.textColor = [107, 114, 128];
                        }
                        if (data.column.index === 5) {
                            const val = String(data.cell.raw);
                            data.cell.styles.fontStyle = 'bold';
                            if (val === 'Fixed') data.cell.styles.textColor = [5, 150, 105];
                            else if (val === 'Regression' || val === 'New Fail') data.cell.styles.textColor = [220, 38, 38];
                            else data.cell.styles.textColor = [107, 114, 128];
                        }
                    }
                });
                y = ((doc as any).lastAutoTable?.finalY ?? y) + 10;
            }

            // Baseline scenarios table
            const baselineScens = this.baselineScenarios();
            if (baselineScens.length > 0) {
                if (y > 240) { doc.addPage(); y = 20; }
                y = this.pdfSectionTitle(doc, 3, 'Baseline Run Scenarios', y);
                autoTable(doc, {
                    startY: y,
                    head: [['#', 'Scenario', 'Steps', 'Passed', 'Failed', 'Status']],
                    body: baselineScens.map((s, i) => [i + 1, s.scenarioName, s.totalSteps, s.passed, s.failed, s.status]),
                    ...this.pdfTableStyles(),
                    columnStyles: { 0: { cellWidth: 8 }, 1: { fontStyle: 'bold' }, 5: { cellWidth: 20 } },
                    didParseCell: (data: any) => {
                        if (data.section !== 'body') return;
                        if (data.column.index === 5) {
                            data.cell.styles.fontStyle = 'bold';
                            if (data.cell.raw === 'Passed') { data.cell.styles.textColor = [5, 150, 105]; data.cell.styles.fillColor = [209, 250, 229]; }
                            else if (data.cell.raw === 'Failed') { data.cell.styles.textColor = [220, 38, 38]; data.cell.styles.fillColor = [254, 226, 226]; }
                            else { data.cell.styles.textColor = [107, 114, 128]; data.cell.styles.fillColor = [243, 244, 246]; }
                        }
                    }
                });
                y = ((doc as any).lastAutoTable?.finalY ?? y) + 10;
            }

            // Current scenarios table
            const currentScens = this.currentScenarios();
            if (currentScens.length > 0) {
                if (y > 240) { doc.addPage(); y = 20; }
                y = this.pdfSectionTitle(doc, 4, 'Current Run Scenarios', y);
                autoTable(doc, {
                    startY: y,
                    head: [['#', 'Scenario', 'Steps', 'Passed', 'Failed', 'Status']],
                    body: currentScens.map((s, i) => [i + 1, s.scenarioName, s.totalSteps, s.passed, s.failed, s.status]),
                    ...this.pdfTableStyles(),
                    columnStyles: { 0: { cellWidth: 8 }, 1: { fontStyle: 'bold' }, 5: { cellWidth: 20 } },
                    didParseCell: (data: any) => {
                        if (data.section !== 'body') return;
                        if (data.column.index === 5) {
                            data.cell.styles.fontStyle = 'bold';
                            if (data.cell.raw === 'Passed') { data.cell.styles.textColor = [5, 150, 105]; data.cell.styles.fillColor = [209, 250, 229]; }
                            else if (data.cell.raw === 'Failed') { data.cell.styles.textColor = [220, 38, 38]; data.cell.styles.fillColor = [254, 226, 226]; }
                            else { data.cell.styles.textColor = [107, 114, 128]; data.cell.styles.fillColor = [243, 244, 246]; }
                        }
                    }
                });
                y = ((doc as any).lastAutoTable?.finalY ?? y) + 10;
            }

            // Functional area breakdown
            const faBreakdown = this.previewFunctionalAreaBreakdown();
            if (faBreakdown.length > 0) {
                if (y > 240) { doc.addPage(); y = 20; }
                y = this.pdfSectionTitle(doc, 5, 'Pass Rate by Functional Area', y);
                autoTable(doc, {
                    startY: y,
                    head: [['Functional Area', 'Scenarios', 'Passed', 'Failed', 'Pass Rate']],
                    body: faBreakdown.map(fa => [fa.name, fa.scenarios, fa.passed, fa.failed, `${fa.passRate}%`]),
                    ...this.pdfTableStyles(),
                    columnStyles: { 0: { fontStyle: 'bold' }, 4: { fontStyle: 'bold' } },
                    didParseCell: (data: any) => {
                        if (data.section !== 'body') return;
                        if (data.column.index === 2) {
                            data.cell.styles.textColor = [5, 150, 105];
                            data.cell.styles.fontStyle = 'bold';
                        }
                        if (data.column.index === 3) {
                            const v = Number(data.cell.raw) || 0;
                            if (v > 0) {
                                data.cell.styles.textColor = [220, 38, 38];
                                data.cell.styles.fontStyle = 'bold';
                            }
                        }
                        if (data.column.index === 4) {
                            const rate = Number(data.cell.raw) || 0;
                            data.cell.styles.fontStyle = 'bold';
                            if (rate >= 80) data.cell.styles.textColor = [5, 150, 105];
                            else if (rate >= 50) data.cell.styles.textColor = [217, 119, 6];
                            else data.cell.styles.textColor = [220, 38, 38];
                        }
                    }
                });
            }
        } else {
        const faBreakdown = this.previewFunctionalAreaBreakdown();
        const reportType = this.selectedReportType();
        const detailLevel = this.selectedDetailLevel() || this.viewingReport()?.detailLevel || 'scenario';
        let sectionTitle = 'Scenario Results';
        let tableHead: string[] = [];
        let tableBody: any[] = [];
        let statusColIndex = 6;

        if (reportType === 'filtered' || reportType === 'execution-summary') {
            sectionTitle = 'Scenario Results';
            tableHead = ['#', 'Functional Area', 'Scenario', 'Package', 'Test Type', 'Steps', 'Passed', 'Failed', 'Status'];
            tableBody = scenarios.map((s, i) => [
                i + 1,
                s.functionalAreaName,
                s.scenarioName,
                s.packageName || '—',
                s.testTypeName || '—',
                s.totalSteps,
                s.passed,
                s.failed,
                s.status
            ]);
            statusColIndex = 8;
        } else if (reportType === 'trend') {
            sectionTitle = 'Scenario Trends';
            tableHead = ['#', 'Scenario', 'Functional Area', 'Pass Rate', 'Prev Pass Rate', 'Delta', 'Stability', 'Status'];
            tableBody = scenarios.map((s, i) => [
                i + 1,
                s.scenarioName,
                s.functionalAreaName,
                s.passRate !== undefined ? `${s.passRate}%` : '—',
                s.prevPassRate !== undefined ? `${s.prevPassRate}%` : '—',
                this.formatDelta(s.delta),
                this.getStabilityLabel(s.stabilityTag),
                s.status
            ]);
            statusColIndex = 7;
        } else if (reportType === 'failure-analysis') {
            sectionTitle = 'Failure Analysis';
            tableHead = ['#', 'Scenario', 'Functional Area', 'Failed Steps', 'Error Category', 'Recurrence', 'First Failed', 'Last Failed'];
            tableBody = scenarios.map((s, i) => [
                i + 1,
                s.scenarioName,
                s.functionalAreaName,
                `${s.failed} / ${s.totalSteps}`,
                s.errorCategory || '—',
                `${s.failureCount || 0} / ${s.totalRunsAnalyzed || 0}`,
                s.firstFailedDate ? new Date(s.firstFailedDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—',
                s.lastFailedDate ? new Date(s.lastFailedDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
            ]);
            statusColIndex = -1;
        }

        if (detailLevel === 'scenario' || detailLevel === 'step') {
        y = this.pdfSectionTitle(doc, 2, sectionTitle, y);
        autoTable(doc, {
            startY: y,
            head: [tableHead],
            body: tableBody,
            ...this.pdfTableStyles(),
            columnStyles: { 0: { cellWidth: 14, fontStyle: 'bold', textColor: [100, 116, 139], halign: 'center' }, 1: { fontStyle: 'bold' }, ...(statusColIndex >= 0 ? { [statusColIndex]: { cellWidth: 24 } } : {}) },
            didParseCell: (data: any) => {
                if (data.section !== 'body') return;
                const colIdx = data.column.index;

                if (reportType === 'filtered' || reportType === 'execution-summary') {
                    if (colIdx === 6) { // Passed
                        const v = Number(data.cell.raw) || 0;
                        data.cell.styles.textColor = v > 0 ? [5, 150, 105] : [148, 163, 184];
                        if (v > 0) data.cell.styles.fontStyle = 'bold';
                    }
                    if (colIdx === 7) { // Failed
                        const v = Number(data.cell.raw) || 0;
                        data.cell.styles.textColor = v > 0 ? [220, 38, 38] : [148, 163, 184];
                        if (v > 0) data.cell.styles.fontStyle = 'bold';
                    }
                } else if (reportType === 'trend') {
                    if (colIdx === 3) { // Pass Rate
                        const rate = Number(String(data.cell.raw).replace('%', '')) || 0;
                        data.cell.styles.fontStyle = 'bold';
                        if (rate >= 80) data.cell.styles.textColor = [5, 150, 105];
                        else if (rate >= 50) data.cell.styles.textColor = [217, 119, 6];
                        else data.cell.styles.textColor = [220, 38, 38];
                    }
                    if (colIdx === 4) { // Prev Pass Rate
                        const rate = Number(String(data.cell.raw).replace('%', '')) || 0;
                        data.cell.styles.fontStyle = 'bold';
                        if (rate >= 80) data.cell.styles.textColor = [5, 150, 105];
                        else if (rate >= 50) data.cell.styles.textColor = [217, 119, 6];
                        else data.cell.styles.textColor = [220, 38, 38];
                    }
                    if (colIdx === 5) { // Delta
                        const val = String(data.cell.raw);
                        data.cell.styles.fontStyle = 'bold';
                        if (val.startsWith('+')) data.cell.styles.textColor = [5, 150, 105];
                        else if (val.startsWith('-')) data.cell.styles.textColor = [220, 38, 38];
                        else data.cell.styles.textColor = [107, 114, 128];
                    }
                    if (colIdx === 6) { // Stability
                        const val = String(data.cell.raw);
                        data.cell.styles.fontStyle = 'bold';
                        if (val === 'Regressing') data.cell.styles.textColor = [220, 38, 38];
                        else if (val === 'Flaky') data.cell.styles.textColor = [217, 119, 6];
                        else if (val === 'Improving') data.cell.styles.textColor = [5, 150, 105];
                        else data.cell.styles.textColor = [5, 150, 105];
                    }
                } else if (reportType === 'failure-analysis') {
                    if (colIdx === 3) { // Failed Steps
                        data.cell.styles.textColor = [220, 38, 38];
                        data.cell.styles.fontStyle = 'bold';
                    }
                    if (colIdx === 5) { // Recurrence
                        data.cell.styles.fontStyle = 'bold';
                    }
                }

                if (colIdx === statusColIndex && reportType !== 'failure-analysis') {
                    const val = data.cell.raw;
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fontSize = 8;
                    if (val === 'Passed') { data.cell.styles.textColor = [5, 150, 105]; data.cell.styles.fillColor = [209, 250, 229]; }
                    else if (val === 'Failed') { data.cell.styles.textColor = [220, 38, 38]; data.cell.styles.fillColor = [254, 226, 226]; }
                    else { data.cell.styles.textColor = [107, 114, 128]; data.cell.styles.fillColor = [243, 244, 246]; }
                }
            }
        });
        } // end scenario/step detail level

        // Section 3: Functional Area Breakdown
        if ((detailLevel === 'functional-area' || detailLevel === 'scenario' || detailLevel === 'step') && faBreakdown.length > 0) {
            y = ((doc as any).lastAutoTable?.finalY ?? y) + 12;
            if (y > 250) { doc.addPage(); y = 20; }
            y = this.pdfSectionTitle(doc, 3, 'Results by Functional Area', y);
            autoTable(doc, {
                startY: y,
                head: [['Functional Area', 'Scenarios', 'Steps', 'Passed', 'Failed', 'Stopped', 'Pass Rate']],
                body: faBreakdown.map(fa => [fa.name, fa.scenarios, fa.totalSteps, fa.passed, fa.failed, fa.stopped, `${fa.passRate}%`]),
                ...this.pdfTableStyles(),
                columnStyles: { 0: { fontStyle: 'bold' } },
                didParseCell: (data: any) => {
                    if (data.section !== 'body') return;
                    if (data.column.index === 3) {
                        const v = Number(data.cell.raw) || 0;
                        data.cell.styles.textColor = v > 0 ? [5, 150, 105] : [148, 163, 184];
                        if (v > 0) data.cell.styles.fontStyle = 'bold';
                    }
                    if (data.column.index === 4) {
                        const v = Number(data.cell.raw) || 0;
                        data.cell.styles.textColor = v > 0 ? [220, 38, 38] : [148, 163, 184];
                        if (v > 0) data.cell.styles.fontStyle = 'bold';
                    }
                    if (data.column.index === 5) {
                        data.cell.styles.textColor = [148, 163, 184];
                    }
                    if (data.column.index === 6) {
                        const rate = Number(String(data.cell.raw).replace('%', '')) || 0;
                        data.cell.styles.fontStyle = 'bold';
                        if (rate >= 80) data.cell.styles.textColor = [5, 150, 105];
                        else if (rate >= 50) data.cell.styles.textColor = [217, 119, 6];
                        else data.cell.styles.textColor = [220, 38, 38];
                    }
                }
            });
        }
        const ttBreakdownPdf = this.previewTestTypeBreakdown();
        if ((detailLevel === 'test-type' || detailLevel === 'scenario' || detailLevel === 'step') && ttBreakdownPdf.length > 0) {
            y = ((doc as any).lastAutoTable?.finalY ?? y) + 12;
            if (y > 250) { doc.addPage(); y = 20; }
            y = this.pdfSectionTitle(doc, 4, 'Results by Test Type', y);
            autoTable(doc, {
                startY: y,
                head: [['Test Type', 'Scenarios', 'Steps', 'Passed', 'Failed', 'Stopped', 'Pass Rate']],
                body: ttBreakdownPdf.map(tt => [tt.name, tt.scenarios, tt.totalSteps, tt.passed, tt.failed, tt.stopped, `${tt.passRate}%`]),
                ...this.pdfTableStyles(),
                columnStyles: { 0: { fontStyle: 'bold' } },
                didParseCell: (data: any) => {
                    if (data.section !== 'body') return;
                    if (data.column.index === 3) {
                        const v = Number(data.cell.raw) || 0;
                        data.cell.styles.textColor = v > 0 ? [5, 150, 105] : [148, 163, 184];
                        if (v > 0) data.cell.styles.fontStyle = 'bold';
                    }
                    if (data.column.index === 4) {
                        const v = Number(data.cell.raw) || 0;
                        data.cell.styles.textColor = v > 0 ? [220, 38, 38] : [148, 163, 184];
                        if (v > 0) data.cell.styles.fontStyle = 'bold';
                    }
                    if (data.column.index === 5) {
                        data.cell.styles.textColor = [148, 163, 184];
                    }
                    if (data.column.index === 6) {
                        const rate = Number(String(data.cell.raw).replace('%', '')) || 0;
                        data.cell.styles.fontStyle = 'bold';
                        if (rate >= 80) data.cell.styles.textColor = [5, 150, 105];
                        else if (rate >= 50) data.cell.styles.textColor = [217, 119, 6];
                        else data.cell.styles.textColor = [220, 38, 38];
                    }
                }
            });
        }
        } // end else (non-comparison)

        // Section 5: Step Details (step level or screenshots)
        const isStepLevelPdf = this.selectedDetailLevel() === 'step' || (this.viewingReport()?.detailLevel === 'step');
        if (isStepLevelPdf || this.includeScreenshots()) {
            const allStepRows = scenarios
                .filter(s => s.steps && s.steps.length > 0)
                .flatMap(s => (s.steps || [])
                    .filter(step => isStepLevelPdf || step.screenshotUrl)
                    .map(step => ({ scenario: s.scenarioName, ...step }))
                );
            if (allStepRows.length > 0) {
                y = (doc as any).lastAutoTable?.finalY ? (doc as any).lastAutoTable.finalY + 12 : y + 12;
                if (y > 250) { doc.addPage(); y = 20; }
                const sectionNum = this.isComparisonMode() ? 5 : (this.previewFunctionalAreaBreakdown().length > 0 ? 4 : 3);
                const sectionTitle = isStepLevelPdf ? 'Step Details' : 'Step Screenshots';
                y = this.pdfSectionTitle(doc, sectionNum, sectionTitle, y);

                const hasScreenshots = this.includeScreenshots();
                const head = [['Step #', 'Scenario', 'Description', 'Status', 'Error']];
                const body = allStepRows.map(s => [s.stepOrder, s.scenario, s.stepDescription, s.status, s.error || '']);

                autoTable(doc, {
                    startY: y,
                    head,
                    body,
                    ...this.pdfTableStyles(),
                    tableWidth: 'auto',
                    columnStyles: {
                        0: { cellWidth: 14, halign: 'center', fontStyle: 'bold', textColor: [100, 116, 139] },
                        1: { cellWidth: 44 },
                        2: { cellWidth: 62 },
                        3: { cellWidth: 22, halign: 'center' },
                        4: { cellWidth: 40, fontSize: 7, textColor: [220, 38, 38] }
                    },
                    didParseCell: (data: any) => {
                        if (data.section !== 'body' || data.column.index !== 3) return;
                        const val = data.cell.raw;
                        data.cell.styles.fontStyle = 'bold';
                        if (val === 'Passed') { data.cell.styles.textColor = [5, 150, 105]; }
                        else if (val === 'Failed') { data.cell.styles.textColor = [220, 38, 38]; }
                        else { data.cell.styles.textColor = [107, 114, 128]; }
                    }
                });

                if (hasScreenshots) {
                    const screenshotSteps = allStepRows.filter(s => s.screenshotUrl);
                    if (screenshotSteps.length > 0) {
                        y = ((doc as any).lastAutoTable?.finalY ?? y) + 10;
                        if (y > 250) { doc.addPage(); y = 20; }
                        y = this.pdfSectionTitle(doc, sectionNum + 1, 'Screenshots', y);

                        for (let si = 0; si < screenshotSteps.length; si++) {
                            const step = screenshotSteps[si];
                            if (si > 0) { doc.addPage(); y = 20; }
                            else if (y > 200) { doc.addPage(); y = 20; }
                            doc.setFontSize(9);
                            doc.setFont('helvetica', 'bold');
                            doc.setTextColor(30, 41, 59);
                            doc.text(`${step.scenario} — Step ${step.stepOrder}: ${step.stepDescription}`, 14, y);
                            y += 5;
                            doc.setFontSize(8);
                            doc.setFont('helvetica', 'normal');
                            const badgeColor = step.status === 'Passed' ? [5, 150, 105] : step.status === 'Failed' ? [220, 38, 38] : [107, 114, 128];
                            doc.setTextColor(badgeColor[0], badgeColor[1], badgeColor[2]);
                            doc.text(step.status, 14, y);
                            y += 4;

                            const imgData = await this.fetchImageAsBase64(step.screenshotUrl!);
                            if (imgData) {
                                try {
                                    const format = imgData.includes('image/png') ? 'PNG' : 'JPEG';
                                    const img = new Image();
                                    await new Promise<void>((resolve) => {
                                        img.onload = () => resolve();
                                        img.onerror = () => resolve();
                                        img.src = imgData;
                                    });
                                    const maxW = pageWidth - 28;
                                    const maxH = 200;
                                    let imgW = img.naturalWidth || 800;
                                    let imgH = img.naturalHeight || 400;
                                    const ratio = Math.min(maxW / imgW, maxH / imgH);
                                    imgW = imgW * ratio;
                                    imgH = imgH * ratio;
                                    doc.addImage(imgData, format, 14, y, imgW, imgH);
                                    y += imgH + 5;
                                } catch {
                                    doc.setTextColor(148, 163, 184);
                                    doc.setFontSize(8);
                                    doc.text('[Screenshot could not be embedded]', 14, y);
                                    y += 6;
                                }
                            } else {
                                doc.setTextColor(148, 163, 184);
                                doc.setFontSize(8);
                                doc.text('[Screenshot unavailable]', 14, y);
                                y += 6;
                            }
                            y += 6;
                        }
                    }
                }
            }
        }

        // Footer on all pages
        const pageCount = doc.getNumberOfPages();
        const genDate = new Date().toLocaleString();
        const pageHeight = doc.internal.pageSize.getHeight();
        const footerY = pageHeight - 13;
        const centerX = pageWidth / 2;
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setDrawColor(226, 232, 240);
            doc.setLineWidth(0.4);
            doc.line(14, footerY, rightMargin, footerY);
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(148, 163, 184);
            doc.text('Test Accelerator — Confidential', 14, footerY + 5);
            doc.text(`Page ${i} of ${pageCount}`, centerX, footerY + 5, { align: 'center' });
            doc.text(`Generated: ${genDate}`, rightMargin, footerY + 5, { align: 'right' });
        }

        const fileReportType = reportType === 'filtered' ? 'custom-report' : reportType;
        const fileName = `${fileReportType}-${new Date().toISOString().slice(0, 10)}.pdf`;
        doc.save(fileName);
        this.messageService.success(`PDF downloaded: ${fileName}`);
    }

    // ===== Export: Excel =====

    async exportExcel(): Promise<void> {
        const workbook = new ExcelJS.Workbook();
        const summary = this.previewSummary();
        const scenarios = this.previewScenarios();
        const faBreakdown = this.previewFunctionalAreaBreakdown();

        const summarySheet = workbook.addWorksheet('Summary');
        summarySheet.getColumn(1).width = 20;
        summarySheet.getColumn(2).width = 25;

        // Row 1: Logo + Title
        try {
            const logoFull = await this.getLogoBase64();
            const logoBase64Data = logoFull.split(',')[1];
            const logoId = workbook.addImage({ base64: logoBase64Data, extension: 'png' });
            summarySheet.addImage(logoId, { tl: { col: 0, row: 0 }, ext: { width: 120, height: 48 } });
        } catch (_) {}
        const titleRow = summarySheet.getRow(1);
        titleRow.height = 40;
        titleRow.getCell(2).value = 'Test Accelerator';
        titleRow.getCell(2).font = { bold: true, size: 18, color: { argb: 'FF1E293B' } };
        titleRow.getCell(2).alignment = { vertical: 'middle' };

        const reportType = this.selectedReportType();

        // Row 2: blank
        summarySheet.addRow([]);

        // Row 3: Report name bold merged across columns A:B
        const reportLabel = REPORT_TYPE_OPTIONS.find(o => o.id === reportType)?.label || reportType;
        const rtRow = summarySheet.addRow([reportLabel]);
        rtRow.getCell(1).font = { bold: true, size: 12, color: { argb: 'FF1E293B' } };
        rtRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
        summarySheet.mergeCells(rtRow.number, 1, rtRow.number, 2);

        // Row 4-5: Generated, Generated By
        const genRow = summarySheet.addRow(['Generated', new Date().toLocaleString()]);
        genRow.getCell(1).font = { bold: true, size: 11 };
        genRow.getCell(2).font = { size: 11 };
        const genByRow = summarySheet.addRow(['Generated By', this.getCurrentUserName()]);
        genByRow.getCell(1).font = { bold: true, size: 11 };
        genByRow.getCell(2).font = { size: 11 };

        // Blank row
        summarySheet.addRow([]);

        // Report Configuration title + details
        const excelConfig = this.getReportConfigDetails(this.viewingReport()).filter(c => c.label !== 'Report Type');
        if (excelConfig.length > 0) {
            const configTitleRow = summarySheet.addRow(['Report Configuration']);
            configTitleRow.getCell(1).font = { bold: true, size: 12, color: { argb: 'FF1E293B' } };
            configTitleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
            summarySheet.mergeCells(configTitleRow.number, 1, configTitleRow.number, 2);
            excelConfig.forEach(cfg => {
                const row = summarySheet.addRow([cfg.label, cfg.value]);
                row.getCell(1).font = { bold: true, size: 11 };
                row.getCell(2).font = { size: 11 };
            });
            summarySheet.addRow([]);
        }

        // Row 8: Metric/Value header
        const headerRow = summarySheet.addRow(['Metric', 'Value']);
        headerRow.getCell(1).font = { bold: true, size: 11, color: { argb: 'FF4A3728' } };
        headerRow.getCell(2).font = { bold: true, size: 11, color: { argb: 'FF4A3728' } };
        headerRow.getCell(1).fill = { type: 'gradient', gradient: 'angle', degree: 0, stops: [{ position: 0, color: { argb: 'FFF5F0EB' } }, { position: 1, color: { argb: 'FFE8DDD4' } }] } as any;
        headerRow.getCell(2).fill = { type: 'gradient', gradient: 'angle', degree: 0, stops: [{ position: 0, color: { argb: 'FFF5F0EB' } }, { position: 1, color: { argb: 'FFE8DDD4' } }] } as any;
        headerRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
        headerRow.getCell(2).alignment = { vertical: 'middle', horizontal: 'center' };
        headerRow.height = 32;

        // Row 9+: KPI data
        const kpiRows: { metric: string; value: string | number; color: string; bg: string }[] = [
            { metric: 'Total Scenarios', value: summary.totalScenarios, color: 'FF1A56DB', bg: 'FFE8EEFB' },
            { metric: 'Total Steps', value: summary.totalSteps, color: 'FF1A56DB', bg: 'FFE8EEFB' },
            { metric: 'Passed', value: summary.totalPassed, color: 'FF059669', bg: 'FFD1FAE5' },
            { metric: 'Failed', value: summary.totalFailed, color: 'FFDC2626', bg: 'FFFEE2E2' },
            { metric: 'Stopped', value: summary.totalStopped, color: 'FF6B7280', bg: 'FFF3F4F6' },
            { metric: 'Pass Rate', value: `${summary.passRate}%`, color: 'FF059669', bg: 'FFF0FDF4' },
        ];

        if (reportType === 'failure-analysis') {
            const faSummary = this.failureAnalysisSummary();
            kpiRows.push(
                { metric: '', value: '', color: 'FF000000', bg: 'FFFFFFFF' },
                { metric: 'Total Failed', value: faSummary.totalFailed, color: 'FFDC2626', bg: 'FFFEE2E2' },
                { metric: 'Unique Failures', value: faSummary.uniqueFailures, color: 'FFD97706', bg: 'FFFFFBEB' },
                { metric: 'Recurring (3+ runs)', value: faSummary.recurring, color: 'FFDC2626', bg: 'FFFEE2E2' },
                { metric: 'Most Impacted Area', value: faSummary.mostImpactedArea, color: 'FF1A56DB', bg: 'FFE8EEFB' },
                { metric: 'Avg Failure Rate', value: `${faSummary.avgFailureRate}%`, color: 'FFDC2626', bg: 'FFFEE2E2' },
            );
        } else if (reportType === 'trend') {
            const tSummary = this.trendSummary();
            kpiRows.push(
                { metric: '', value: '', color: 'FF000000', bg: 'FFFFFFFF' },
                { metric: 'Runs Analyzed', value: tSummary.runsAnalyzed, color: 'FF1A56DB', bg: 'FFE8EEFB' },
                { metric: 'Avg Pass Rate', value: `${tSummary.avgPassRate}%`, color: 'FF059669', bg: 'FFD1FAE5' },
                { metric: 'Trend Direction', value: `${tSummary.trendDirection >= 0 ? '+' : ''}${tSummary.trendDirection}%`, color: tSummary.trendDirection >= 0 ? 'FF059669' : 'FFDC2626', bg: tSummary.trendDirection >= 0 ? 'FFD1FAE5' : 'FFFEE2E2' },
                { metric: 'Flaky Tests', value: tSummary.flakyTests, color: 'FFD97706', bg: 'FFFFFBEB' },
                { metric: 'Regressions', value: tSummary.regressions, color: 'FFDC2626', bg: 'FFFEE2E2' },
            );
        }

        kpiRows.forEach(kpi => {
            if (!kpi.metric) { summarySheet.addRow([]); return; }
            const row = summarySheet.addRow([kpi.metric, kpi.value]);
            row.getCell(2).font = { bold: true, size: 14, color: { argb: kpi.color } };
            row.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: kpi.bg } };
            row.getCell(2).alignment = { horizontal: 'center', vertical: 'middle' };
            row.getCell(1).font = { bold: true, size: 11 };
        });
        const detailLevel = this.selectedDetailLevel() || this.viewingReport()?.detailLevel || 'scenario';
        if (this.isComparisonMode()) {
            // Comparison sheet
            const compSheet = workbook.addWorksheet('Comparison');
            compSheet.columns = [
                { header: '#', key: 'num', width: 5 },
                { header: 'Scenario', key: 'name', width: 40 },
                { header: 'Functional Area', key: 'fa', width: 25 },
                { header: 'Before (Baseline)', key: 'before', width: 16 },
                { header: 'After (Current)', key: 'after', width: 16 },
                { header: 'Change', key: 'change', width: 14 },
                { header: 'Details', key: 'details', width: 30 }
            ];
            const compSummary = this.comparisonSummary();
            compSheet.addRow({ num: '', name: 'Baseline Run', fa: this.getBaselineRunLabel(), before: `${compSummary.beforePassRate}%`, after: '', change: '', details: '' });
            compSheet.addRow({ num: '', name: 'Current Run', fa: this.getComparisonRunLabel(), before: '', after: `${compSummary.afterPassRate}%`, change: `Delta: ${compSummary.passRateDelta >= 0 ? '+' : ''}${compSummary.passRateDelta}%`, details: '' });
            compSheet.addRow({});
            this.comparisonDeltas().forEach((d, i) => {
                const row = compSheet.addRow({ num: i + 1, name: d.scenarioName, fa: d.functionalAreaName, before: d.beforeStatus, after: d.afterStatus, change: this.getChangeTagLabel(d.changeType), details: d.details });
                this.styleExcelStatusCell(row.getCell('before'), d.beforeStatus);
                this.styleExcelStatusCell(row.getCell('after'), d.afterStatus);
                const changeCell = row.getCell('change');
                if (d.changeType === 'fixed') changeCell.font = { bold: true, color: { argb: 'FF059669' } };
                else if (d.changeType === 'regression' || d.changeType === 'new-fail') changeCell.font = { bold: true, color: { argb: 'FFDC2626' } };
                else changeCell.font = { color: { argb: 'FF6B7280' } };
            });
            this.styleExcelHeader(compSheet);

            // Baseline Run sheet
            const baselineSheet = workbook.addWorksheet('Baseline Run');
            baselineSheet.columns = [
                { header: '#', key: 'num', width: 5 },
                { header: 'Scenario', key: 'name', width: 40 },
                { header: 'Steps', key: 'steps', width: 8 },
                { header: 'Passed', key: 'passed', width: 10 },
                { header: 'Failed', key: 'failed', width: 10 },
                { header: 'Status', key: 'status', width: 12 }
            ];
            this.baselineScenarios().forEach((s, i) => {
                const row = baselineSheet.addRow({ num: i + 1, name: s.scenarioName, steps: s.totalSteps, passed: s.passed, failed: s.failed, status: s.status });
                this.styleExcelStatusCell(row.getCell('status'), s.status);
            });
            this.styleExcelHeader(baselineSheet);

            // Current Run sheet
            const currentSheet = workbook.addWorksheet('Current Run');
            currentSheet.columns = [
                { header: '#', key: 'num', width: 5 },
                { header: 'Scenario', key: 'name', width: 40 },
                { header: 'Steps', key: 'steps', width: 8 },
                { header: 'Passed', key: 'passed', width: 10 },
                { header: 'Failed', key: 'failed', width: 10 },
                { header: 'Status', key: 'status', width: 12 }
            ];
            this.currentScenarios().forEach((s, i) => {
                const row = currentSheet.addRow({ num: i + 1, name: s.scenarioName, steps: s.totalSteps, passed: s.passed, failed: s.failed, status: s.status });
                this.styleExcelStatusCell(row.getCell('status'), s.status);
            });
            this.styleExcelHeader(currentSheet);
        } else {
        if (detailLevel === 'scenario' || detailLevel === 'step') {
        const scenarioSheet = workbook.addWorksheet('Scenarios');
        const reportType = this.selectedReportType();
        let columns: any[] = [];
        let rowMapper: (s: any, i: number) => any;

        if (reportType === 'filtered' || reportType === 'execution-summary') {
            columns = [
                { header: '#', key: 'num', width: 5 },
                { header: 'Functional Area', key: 'fa', width: 25 },
                { header: 'Scenario', key: 'name', width: 40 },
                { header: 'Package', key: 'pkg', width: 20 },
                { header: 'Test Type', key: 'type', width: 20 },
                { header: 'Steps', key: 'steps', width: 8 },
                { header: 'Passed', key: 'passed', width: 10 },
                { header: 'Failed', key: 'failed', width: 10 },
                { header: 'Status', key: 'status', width: 12 }
            ];
            rowMapper = (s, i) => ({
                num: i + 1, fa: s.functionalAreaName, name: s.scenarioName,
                pkg: s.packageName || '—', type: s.testTypeName || '—',
                steps: s.totalSteps, passed: s.passed, failed: s.failed,
                status: s.status
            });
        } else if (reportType === 'trend') {
            columns = [
                { header: '#', key: 'num', width: 5 },
                { header: 'Scenario', key: 'name', width: 40 },
                { header: 'Functional Area', key: 'fa', width: 25 },
                { header: 'Run Date', key: 'runDate', width: 15 },
                { header: 'Run ID', key: 'runId', width: 18 },
                { header: 'Pass Rate', key: 'passRate', width: 12 },
                { header: 'Prev Pass Rate', key: 'prevRate', width: 14 },
                { header: 'Delta', key: 'delta', width: 10 },
                { header: 'Stability', key: 'stability', width: 14 },
                { header: 'Status', key: 'status', width: 12 }
            ];
            rowMapper = (s, i) => ({
                num: i + 1, name: s.scenarioName, fa: s.functionalAreaName,
                runDate: s.executedOn ? new Date(s.executedOn).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—',
                runId: s.executionId || '—',
                passRate: s.passRate !== undefined ? `${s.passRate}%` : '—',
                prevRate: s.prevPassRate !== undefined ? `${s.prevPassRate}%` : '—',
                delta: this.formatDelta(s.delta),
                stability: this.getStabilityLabel(s.stabilityTag),
                status: s.status
            });
        } else if (reportType === 'failure-analysis') {
            columns = [
                { header: '#', key: 'num', width: 5 },
                { header: 'Scenario', key: 'name', width: 40 },
                { header: 'Functional Area', key: 'fa', width: 25 },
                { header: 'Failed Steps', key: 'failedSteps', width: 14 },
                { header: 'Error Category', key: 'errorCat', width: 18 },
                { header: 'Recurrence', key: 'recurrence', width: 14 },
                { header: 'First Failed', key: 'firstFail', width: 14 },
                { header: 'Last Failed', key: 'lastFail', width: 14 },
                { header: 'Root Cause', key: 'rootCause', width: 18 },
                { header: 'Severity', key: 'severity', width: 12 }
            ];
            rowMapper = (s, i) => ({
                num: i + 1, name: s.scenarioName, fa: s.functionalAreaName,
                failedSteps: `${s.failed} / ${s.totalSteps}`,
                errorCat: s.errorCategory || '—',
                recurrence: `${s.failureCount || 0} / ${s.totalRunsAnalyzed || 0}`,
                firstFail: s.firstFailedDate ? new Date(s.firstFailedDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—',
                lastFail: s.lastFailedDate ? new Date(s.lastFailedDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—',
                rootCause: s.rootCause || '—',
                severity: this.getSeverityLabel(s.severity)
            });
        }

        scenarioSheet.columns = columns;
        const hasStatusCol = columns.some((c: any) => c.key === 'status');
        scenarios.forEach((s, i) => {
            const row = scenarioSheet.addRow(rowMapper(s, i));
            if (hasStatusCol) this.styleExcelStatusCell(row.getCell('status'), s.status);

            if (reportType === 'filtered' || reportType === 'execution-summary') {
                const passedCell = row.getCell('passed');
                passedCell.font = { bold: true, color: { argb: 'FF059669' } };
                const failedCell = row.getCell('failed');
                if (s.failed > 0) failedCell.font = { bold: true, color: { argb: 'FFDC2626' } };
            } else if (reportType === 'trend') {
                const passRateCell = row.getCell('passRate');
                const rate = s.passRate || 0;
                if (rate >= 80) passRateCell.font = { bold: true, color: { argb: 'FF059669' } };
                else if (rate >= 50) passRateCell.font = { bold: true, color: { argb: 'FFD97706' } };
                else passRateCell.font = { bold: true, color: { argb: 'FFDC2626' } };
                const deltaCell = row.getCell('delta');
                const deltaVal = String(deltaCell.value || '');
                if (deltaVal.startsWith('+')) deltaCell.font = { bold: true, color: { argb: 'FF059669' } };
                else if (deltaVal.startsWith('-')) deltaCell.font = { bold: true, color: { argb: 'FFDC2626' } };
                else deltaCell.font = { bold: true, color: { argb: 'FF6B7280' } };
                const stabilityCell = row.getCell('stability');
                const stabVal = String(stabilityCell.value || '');
                if (stabVal === 'Regressing') stabilityCell.font = { bold: true, color: { argb: 'FFDC2626' } };
                else if (stabVal === 'Flaky') stabilityCell.font = { bold: true, color: { argb: 'FFD97706' } };
                else if (stabVal === 'Improving') stabilityCell.font = { bold: true, color: { argb: 'FF059669' } };
                else stabilityCell.font = { color: { argb: 'FF059669' } };
            } else if (reportType === 'failure-analysis') {
                const failedStepsCell = row.getCell('failedSteps');
                failedStepsCell.font = { bold: true, color: { argb: 'FFDC2626' } };
                const recurrenceCell = row.getCell('recurrence');
                recurrenceCell.font = { bold: true };
                const severityCell = row.getCell('severity');
                const sevVal = String(severityCell.value || '');
                if (sevVal === 'Critical') { severityCell.font = { bold: true, color: { argb: 'FFDC2626' } }; severityCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } }; }
                else if (sevVal === 'High') { severityCell.font = { bold: true, color: { argb: 'FFD97706' } }; severityCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } }; }
                else if (sevVal === 'Medium') { severityCell.font = { bold: true, color: { argb: 'FF0284C7' } }; severityCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0F2FE' } }; }
                else { severityCell.font = { color: { argb: 'FF6B7280' } }; }
            }
        });
        this.styleExcelHeader(scenarioSheet);
        }
        }

        if (!this.isComparisonMode() && (detailLevel === 'functional-area' || detailLevel === 'scenario' || detailLevel === 'step') && faBreakdown.length > 0) {
            const faSheet = workbook.addWorksheet('By Functional Area');
            faSheet.columns = [
                { header: 'Functional Area', key: 'name', width: 30 },
                { header: 'Scenarios', key: 'scenarios', width: 12 },
                { header: 'Steps', key: 'totalSteps', width: 10 },
                { header: 'Passed', key: 'passed', width: 10 },
                { header: 'Failed', key: 'failed', width: 10 },
                { header: 'Stopped', key: 'stopped', width: 10 },
                { header: 'Pass Rate', key: 'passRate', width: 12 }
            ];
            faBreakdown.forEach(fa => {
                const row = faSheet.addRow({ ...fa, passRate: `${fa.passRate}%` });
                this.styleExcelPassRate(row.getCell('passRate'), fa.passRate);
                row.getCell('passed').font = { bold: true, color: { argb: 'FF059669' } };
                if (fa.failed > 0) row.getCell('failed').font = { bold: true, color: { argb: 'FFDC2626' } };
                if (fa.stopped > 0) row.getCell('stopped').font = { bold: true, color: { argb: 'FF6B7280' } };
            });
            this.styleExcelHeader(faSheet);
        }

        const ttBreakdown = this.previewTestTypeBreakdown();
        if (!this.isComparisonMode() && (detailLevel === 'test-type' || detailLevel === 'scenario' || detailLevel === 'step') && ttBreakdown.length > 0) {
            const ttSheet = workbook.addWorksheet('By Test Type');
            ttSheet.columns = [
                { header: 'Test Type', key: 'name', width: 30 },
                { header: 'Scenarios', key: 'scenarios', width: 12 },
                { header: 'Steps', key: 'totalSteps', width: 10 },
                { header: 'Passed', key: 'passed', width: 10 },
                { header: 'Failed', key: 'failed', width: 10 },
                { header: 'Stopped', key: 'stopped', width: 10 },
                { header: 'Pass Rate', key: 'passRate', width: 12 }
            ];
            ttBreakdown.forEach(tt => {
                const row = ttSheet.addRow({ ...tt, passRate: `${tt.passRate}%` });
                this.styleExcelPassRate(row.getCell('passRate'), tt.passRate);
                row.getCell('passed').font = { bold: true, color: { argb: 'FF059669' } };
                if (tt.failed > 0) row.getCell('failed').font = { bold: true, color: { argb: 'FFDC2626' } };
                if (tt.stopped > 0) row.getCell('stopped').font = { bold: true, color: { argb: 'FF6B7280' } };
            });
            this.styleExcelHeader(ttSheet);
        }


        const failedScenarios = scenarios.filter(s => s.status === 'Failed');
        if (failedScenarios.length > 0) {
            const errSheet = workbook.addWorksheet('Errors Only');
            errSheet.columns = [
                { header: 'Scenario', key: 'name', width: 40 },
                { header: 'Functional Area', key: 'fa', width: 25 },
                { header: 'Steps', key: 'steps', width: 8 },
                { header: 'Failed', key: 'failed', width: 10 }
            ];
            failedScenarios.forEach(s => {
                errSheet.addRow({ name: s.scenarioName, fa: s.functionalAreaName, steps: s.totalSteps, failed: s.failed });
            });
            this.styleExcelHeader(errSheet);
        }

        const isStepLevelExcel = this.selectedDetailLevel() === 'step' || (this.viewingReport()?.detailLevel === 'step');
        if (isStepLevelExcel || this.includeScreenshots()) {
            const allStepRows = scenarios
                .filter(s => s.steps && s.steps.length > 0)
                .flatMap(s => (s.steps || [])
                    .filter(step => isStepLevelExcel || step.screenshotUrl)
                    .map(step => ({ scenario: s.scenarioName, functionalArea: s.functionalAreaName, ...step }))
                );
            if (allStepRows.length > 0) {
                const sheetName = isStepLevelExcel ? 'Step Details' : 'Screenshots';
                const stepsSheet = workbook.addWorksheet(sheetName);
                const hasScreenshots = this.includeScreenshots();
                stepsSheet.columns = [
                    { header: 'Scenario', key: 'scenario', width: 35 },
                    { header: 'Functional Area', key: 'functionalArea', width: 25 },
                    { header: 'Step #', key: 'stepOrder', width: 8 },
                    { header: 'Description', key: 'stepDescription', width: 40 },
                    { header: 'Status', key: 'status', width: 12 },
                    { header: 'Error', key: 'error', width: 40 },
                    ...(hasScreenshots ? [{ header: 'Screenshot', key: 'screenshotUrl', width: 40 }] : [])
                ];
                for (const s of allStepRows) {
                    const row = stepsSheet.addRow({
                        scenario: s.scenario, functionalArea: s.functionalArea,
                        stepOrder: s.stepOrder, stepDescription: s.stepDescription,
                        status: s.status, error: s.error || ''
                    });
                    this.styleExcelStatusCell(row.getCell('status'), s.status);
                    if (s.error) {
                        row.getCell('error').font = { color: { argb: 'FFDC2626' }, size: 9 };
                    }
                    if (hasScreenshots && s.screenshotUrl) {
                        const imgData = await this.fetchImageAsBase64(s.screenshotUrl);
                        if (imgData) {
                            const base64Only = imgData.split(',')[1];
                            const imgId = workbook.addImage({ base64: base64Only, extension: 'png' });
                            const rowNum = row.number;
                            stepsSheet.addImage(imgId, {
                                tl: { col: 6, row: rowNum - 1 } as any,
                                ext: { width: 240, height: 120 }
                            });
                            row.height = 95;
                        } else {
                            row.getCell('screenshotUrl').value = '[Image unavailable]';
                            row.getCell('screenshotUrl').font = { color: { argb: 'FF94A3B8' }, italic: true };
                        }
                    }
                }
                this.styleExcelHeader(stepsSheet);
            }
        }

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const excelReportType = this.selectedReportType() === 'filtered' ? 'custom-report' : this.selectedReportType();
        link.download = `${excelReportType}-${new Date().toISOString().slice(0, 10)}.xlsx`;
        link.click();
        URL.revokeObjectURL(url);
        this.messageService.success('Excel downloaded');
    }

    private styleExcelHeader(sheet: ExcelJS.Worksheet): void {
        const headerRow = sheet.getRow(1);
        const colCount = sheet.columnCount || (sheet.columns ? sheet.columns.length : 0);
        headerRow.height = 32;
        for (let col = 1; col <= colCount; col++) {
            const cell = headerRow.getCell(col);
            cell.font = { bold: true, size: 11, color: { argb: 'FF4A3728' } };
            cell.fill = {
                type: 'gradient',
                gradient: 'angle',
                degree: 0,
                stops: [
                    { position: 0, color: { argb: 'FFF5F0EB' } },
                    { position: 1, color: { argb: 'FFE8DDD4' } }
                ]
            } as any;
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = {
                bottom: { style: 'medium', color: { argb: 'FFC4A882' } },
                top: { style: 'thin', color: { argb: 'FFE8DDD4' } },
                left: { style: 'thin', color: { argb: 'FFE8DDD4' } },
                right: { style: 'thin', color: { argb: 'FFE8DDD4' } }
            };
        }
    }

    private styleExcelStatusCell(cell: ExcelJS.Cell, status: string): void {
        if (status === 'Passed') {
            cell.font = { bold: true, color: { argb: 'FF059669' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD1FAE5' } };
        } else if (status === 'Failed') {
            cell.font = { bold: true, color: { argb: 'FFDC2626' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } };
        } else {
            cell.font = { bold: true, color: { argb: 'FF6B7280' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
        }
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
    }

    private styleExcelPassRate(cell: ExcelJS.Cell, rate: number): void {
        cell.font = { bold: true, color: { argb: rate >= 80 ? 'FF059669' : rate >= 50 ? 'FFD97706' : 'FFDC2626' } };
        cell.fill = {
            type: 'pattern', pattern: 'solid',
            fgColor: { argb: rate >= 80 ? 'FFF0FDF4' : rate >= 50 ? 'FFFFFBEB' : 'FFFEF2F2' }
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
    }

    private buildHtmlComparisonSection(): string {
        const compSummary = this.comparisonSummary();
        const deltas = this.comparisonDeltas();
        const baselineScens = this.baselineScenarios();
        const currentScens = this.currentScenarios();

        const baselinePassed = baselineScens.filter(s => s.status === 'Passed').length;
        const baselineFailed = baselineScens.filter(s => s.status === 'Failed').length;
        const currentPassed = currentScens.filter(s => s.status === 'Passed').length;
        const currentFailed = currentScens.filter(s => s.status === 'Failed').length;

        const deltaRows = deltas.map((d) => {
            const rowBg = d.changeType === 'fixed' ? 'background:#f0fdf4;' : '';
            const changeTag = d.changeType === 'fixed' ? '<span style="display:inline-block;padding:3px 10px;border-radius:12px;font-size:10px;font-weight:700;text-transform:uppercase;background:#d1fae5;color:#059669">Fixed</span>'
                : d.changeType === 'regression' ? '<span style="display:inline-block;padding:3px 10px;border-radius:12px;font-size:10px;font-weight:700;text-transform:uppercase;background:#fee2e2;color:#dc2626">Regression</span>'
                : d.changeType === 'new-fail' ? '<span style="display:inline-block;padding:3px 10px;border-radius:12px;font-size:10px;font-weight:700;text-transform:uppercase;background:#fef3c7;color:#d97706">New Fail</span>'
                : d.changeType === 'stable-pass' ? '<span style="display:inline-block;padding:3px 10px;border-radius:12px;font-size:10px;font-weight:700;text-transform:uppercase;background:#d1fae5;color:#059669">Stable</span>'
                : '<span style="display:inline-block;padding:3px 10px;border-radius:12px;font-size:10px;font-weight:700;text-transform:uppercase;background:#f3f4f6;color:#6b7280">Stable Fail</span>';
            return `<tr style="${rowBg}">
                <td>${changeTag}</td>
                <td style="font-weight:600">${d.scenarioName}</td>
                <td>${d.functionalAreaName}</td>
                <td><span class="badge badge-${d.beforeStatus === 'Passed' ? 'pass' : d.beforeStatus === 'Failed' ? 'fail' : 'stop'}"><span class="dot"></span>${d.beforeStatus}</span></td>
                <td><span class="badge badge-${d.afterStatus === 'Passed' ? 'pass' : d.afterStatus === 'Failed' ? 'fail' : 'stop'}"><span class="dot"></span>${d.afterStatus}</span></td>
                <td style="font-size:12px;color:#64748b">${d.details}</td>
            </tr>`;
        }).join('');

        const faBreakdown = this.previewFunctionalAreaBreakdown();
        const faRows = faBreakdown.map(fa => {
            const barColor = fa.passRate >= 80 ? '#059669' : fa.passRate >= 50 ? '#d97706' : '#dc2626';
            const rateColor = fa.passRate >= 80 ? '#059669' : fa.passRate >= 50 ? '#d97706' : '#dc2626';
            return `<tr>
                <td style="font-weight:600">${fa.name}</td>
                <td>${fa.scenarios}</td>
                <td style="color:#059669;font-weight:600">${fa.passed}</td>
                <td style="${fa.failed > 0 ? 'color:#dc2626;font-weight:600' : ''}">${fa.failed}</td>
                <td><span style="color:${rateColor};font-weight:700">${fa.passRate}%</span></td>
                <td><div style="width:100%;height:8px;border-radius:4px;background:#e2e8f0;overflow:hidden"><div style="width:${fa.passRate}%;height:100%;background:${barColor};border-radius:4px"></div></div></td>
            </tr>`;
        }).join('');

        return `
        <div class="section-title"><div class="section-num">1</div>Runs Compared</div>
        <div style="display:grid;grid-template-columns:1fr 60px 1fr;gap:0;margin-bottom:24px">
            <div style="padding:20px;border-radius:10px 0 0 10px;border:1px solid #fecaca;background:#fef2f2">
                <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:#dc2626;margin-bottom:8px">Baseline (Before)</div>
                <div style="font-size:14px;font-weight:700;margin-bottom:4px">${this.getBaselineRunLabel()}</div>
                <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:12px">
                    <div><span style="font-size:11px;color:#94a3b8">Scenarios</span><br><span style="font-weight:700">${baselineScens.length}</span></div>
                    <div><span style="font-size:11px;color:#94a3b8">Pass Rate</span><br><span style="font-weight:700;color:${compSummary.beforePassRate >= 70 ? '#059669' : '#dc2626'}">${compSummary.beforePassRate}%</span></div>
                    <div><span style="font-size:11px;color:#94a3b8">Passed</span><br><span style="font-weight:700;color:#059669">${baselinePassed}</span></div>
                    <div><span style="font-size:11px;color:#94a3b8">Failed</span><br><span style="font-weight:700;color:#dc2626">${baselineFailed}</span></div>
                </div>
            </div>
            <div style="display:flex;align-items:center;justify-content:center;border-top:1px solid #e2e8f0;border-bottom:1px solid #e2e8f0;background:#e8eefb;font-weight:800;color:#1a56db;font-size:18px">VS</div>
            <div style="padding:20px;border-radius:0 10px 10px 0;border:1px solid #bbf7d0;background:#f0fdf4">
                <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:#059669;margin-bottom:8px">Current (After)</div>
                <div style="font-size:14px;font-weight:700;margin-bottom:4px">${this.getComparisonRunLabel()}</div>
                <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:12px">
                    <div><span style="font-size:11px;color:#94a3b8">Scenarios</span><br><span style="font-weight:700">${currentScens.length}</span></div>
                    <div><span style="font-size:11px;color:#94a3b8">Pass Rate</span><br><span style="font-weight:700;color:${compSummary.afterPassRate >= 70 ? '#059669' : '#dc2626'}">${compSummary.afterPassRate}%</span></div>
                    <div><span style="font-size:11px;color:#94a3b8">Passed</span><br><span style="font-weight:700;color:#059669">${currentPassed}</span></div>
                    <div><span style="font-size:11px;color:#94a3b8">Failed</span><br><span style="font-weight:700;color:#dc2626">${currentFailed}</span></div>
                </div>
            </div>
        </div>

        <div class="section-title"><div class="section-num">2</div>Change Summary</div>
        <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:24px">
            <div style="padding:16px;border-radius:10px;text-align:center;background:#d1fae5;border:1px solid #6ee7b7">
                <div style="font-size:28px;font-weight:800;color:#059669">+${compSummary.nowPassing}</div>
                <div style="font-size:11px;font-weight:600;color:#64748b;text-transform:uppercase;margin-top:4px">Now Passing</div>
            </div>
            <div style="padding:16px;border-radius:10px;text-align:center;background:#fee2e2;border:1px solid #fca5a5">
                <div style="font-size:28px;font-weight:800;color:#dc2626">${compSummary.newFailures}</div>
                <div style="font-size:11px;font-weight:600;color:#64748b;text-transform:uppercase;margin-top:4px">New Failures</div>
            </div>
            <div style="padding:16px;border-radius:10px;text-align:center;background:#fef3c7;border:1px solid #fde68a">
                <div style="font-size:28px;font-weight:800;color:#d97706">${compSummary.regressions}</div>
                <div style="font-size:11px;font-weight:600;color:#64748b;text-transform:uppercase;margin-top:4px">Regressions</div>
            </div>
            <div style="padding:16px;border-radius:10px;text-align:center;background:#ede9fe;border:1px solid #c4b5fd">
                <div style="font-size:28px;font-weight:800;color:#7c3aed">${compSummary.passRateDelta >= 0 ? '+' : ''}${compSummary.passRateDelta}%</div>
                <div style="font-size:11px;font-weight:600;color:#64748b;text-transform:uppercase;margin-top:4px">Pass Rate Change</div>
            </div>
        </div>

        <div class="section-title"><div class="section-num">3</div>Scenario-Level Changes</div>
        <table><thead><tr><th>Change</th><th>Scenario</th><th>Functional Area</th><th>Before</th><th>After</th><th>Details</th></tr></thead>
        <tbody>${deltaRows}</tbody></table>

        ${faBreakdown.length > 0 ? `
        <div class="section-title"><div class="section-num">4</div>Pass Rate by Functional Area</div>
        <table><thead><tr><th>Functional Area</th><th>Scenarios</th><th>Passed</th><th>Failed</th><th>Pass Rate</th><th style="width:120px">Visual</th></tr></thead>
        <tbody>${faRows}</tbody></table>` : ''}`;
    }

    private buildHtmlScreenshotSection(scenarios: ReportPreviewScenario[]): string {
        const isStepLevel = this.selectedDetailLevel() === 'step' || (this.viewingReport()?.detailLevel === 'step');
        const hasScreenshots = this.includeScreenshots();
        if (!isStepLevel && !hasScreenshots) return '';

        const allStepRows = scenarios
            .filter(s => s.steps && s.steps.length > 0)
            .flatMap(s => (s.steps || [])
                .filter(step => isStepLevel || step.screenshotUrl)
                .map(step => ({ scenario: s.scenarioName, ...step }))
            );
        if (allStepRows.length === 0) return '';

        const rows = allStepRows.map(s => {
            const badgeClass = s.status === 'Passed' ? 'badge-pass' : s.status === 'Failed' ? 'badge-fail' : 'badge-stop';
            const screenshotHtml = (s.screenshotUrl && hasScreenshots)
                ? `<img src="${s.screenshotUrl}" alt="Step ${s.stepOrder} screenshot" style="max-width:100%;border-radius:8px;border:1px solid #e2e8f0;margin-top:8px"/>`
                : '';
            const errorHtml = s.error ? `<div class="step-error">${s.error}</div>` : '';
            return `<div class="step-card">
                <div class="step-card-header">
                    <span style="font-weight:600">${s.scenario}</span>
                    <span class="badge ${badgeClass}"><span class="dot"></span>Step ${s.stepOrder} - ${s.status}</span>
                </div>
                <div class="step-card-desc">${s.stepDescription}</div>
                <div class="step-card-meta">${s.duration}</div>
                ${errorHtml}
                ${screenshotHtml}
            </div>`;
        }).join('');

        const sectionTitle = isStepLevel ? 'Step Details' : 'Step Screenshots';

        return `<div class="section-title"><div class="section-num">4</div>${sectionTitle}</div>
            <div class="steps-grid">${rows}</div>
            <style>.steps-grid{display:grid;grid-template-columns:1fr;gap:12px;margin-bottom:28px}
            .step-card{border:1px solid #e2e8f0;border-radius:10px;padding:16px;background:#fafbfc}
            .step-card-header{display:flex;justify-content:space-between;align-items:center;margin-bottom:4px}
            .step-card-desc{font-size:12px;color:#1e293b;font-weight:500}
            .step-card-meta{font-size:11px;color:#94a3b8;margin-top:2px}
            .step-error{font-size:11px;color:#dc2626;font-family:monospace;margin-top:4px;padding:6px 8px;background:#fef2f2;border-radius:4px}</style>`;
    }

    // ===== Export: HTML =====

    async exportHtml(): Promise<void> {
        const summary = this.previewSummary();
        const scenarios = this.previewScenarios();
        const faBreakdown = this.previewFunctionalAreaBreakdown();

        const reportType = this.selectedReportType();
        const scenarioRows = scenarios.map((s, i) => {
            const badgeClass = s.status === 'Passed' ? 'badge-pass' : s.status === 'Failed' ? 'badge-fail' : 'badge-stop';
            const execDate = s.executedOn ? new Date(s.executedOn).toLocaleDateString() : '—';
            const passRate = s.passRate !== undefined ? `${s.passRate}%` : '—';

            if (reportType === 'filtered' || reportType === 'execution-summary') {
                return `<tr>
                    <td>${i + 1}</td>
                    <td>${s.functionalAreaName}</td>
                    <td style="font-weight:600">${s.scenarioName}</td>
                    <td>${s.packageName || '—'}</td>
                    <td>${s.testTypeName || '—'}</td>
                    <td>${s.totalSteps}</td>
                    <td class="td-pass">${s.passed}</td>
                    <td class="${s.failed > 0 ? 'td-fail' : ''}">${s.failed}</td>
                    <td><span class="badge ${badgeClass}"><span class="dot"></span>${s.status}</span></td>
                </tr>`;
            } else if (reportType === 'trend') {
                const passRateClass = s.passRate && s.passRate >= 80 ? 'td-rate-good' : s.passRate && s.passRate >= 50 ? 'td-rate-warn' : 'td-rate-bad';
                const prevPassRateClass = s.prevPassRate && s.prevPassRate >= 80 ? 'td-rate-good' : s.prevPassRate && s.prevPassRate >= 50 ? 'td-rate-warn' : 'td-rate-bad';
                const deltaVal = s.delta !== undefined && s.delta !== null ? (s.delta >= 0 ? `+${s.delta}%` : `${s.delta}%`) : '—';
                const deltaClass = s.delta !== undefined && s.delta !== null ? (s.delta > 0 ? 'td-pass' : s.delta < 0 ? 'td-fail' : '') : '';
                const stabilityLabel = this.getStabilityLabel(s.stabilityTag);
                return `<tr>
                    <td>${i + 1}</td>
                    <td style="font-weight:600">${s.scenarioName}</td>
                    <td>${s.functionalAreaName}</td>
                    <td class="${passRateClass}">${passRate}</td>
                    <td class="${prevPassRateClass}">${s.prevPassRate !== undefined ? `${s.prevPassRate}%` : '—'}</td>
                    <td class="${deltaClass}">${deltaVal}</td>
                    <td>${stabilityLabel}</td>
                    <td><span class="badge ${badgeClass}"><span class="dot"></span>${s.status}</span></td>
                </tr>`;
            } else if (reportType === 'failure-analysis') {
                const firstFailed = s.firstFailedDate ? new Date(s.firstFailedDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                const lastFailed = s.lastFailedDate ? new Date(s.lastFailedDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                return `<tr>
                    <td>${i + 1}</td>
                    <td style="font-weight:600">${s.scenarioName}</td>
                    <td>${s.functionalAreaName}</td>
                    <td class="td-fail">${s.failed} / ${s.totalSteps}</td>
                    <td>${s.errorCategory || '—'}</td>
                    <td style="font-weight:600">${s.failureCount || 0} / ${s.totalRunsAnalyzed || 0}</td>
                    <td>${firstFailed}</td>
                    <td>${lastFailed}</td>
                </tr>`;
            }
            return '';
        }).join('');

        const faRows = faBreakdown.map(fa => {
            const rateClass = fa.passRate >= 80 ? 'td-rate-good' : fa.passRate >= 50 ? 'td-rate-warn' : 'td-rate-bad';
            return `<tr>
                <td style="font-weight:600">${fa.name}</td>
                <td>${fa.scenarios}</td>
                <td>${fa.totalSteps}</td>
                <td class="td-pass">${fa.passed}</td>
                <td class="${fa.failed > 0 ? 'td-fail' : ''}">${fa.failed}</td>
                <td>${fa.stopped}</td>
                <td class="${rateClass}">${fa.passRate}%</td>
            </tr>`;
        }).join('');

        const ttBreakdown = this.previewTestTypeBreakdown();
        const ttRows = ttBreakdown.map(tt => {
            const rateClass = tt.passRate >= 80 ? 'td-rate-good' : tt.passRate >= 50 ? 'td-rate-warn' : 'td-rate-bad';
            return `<tr>
                <td style="font-weight:600">${tt.name}</td>
                <td>${tt.scenarios}</td>
                <td>${tt.totalSteps}</td>
                <td class="td-pass">${tt.passed}</td>
                <td class="${tt.failed > 0 ? 'td-fail' : ''}">${tt.failed}</td>
                <td>${tt.stopped}</td>
                <td class="${rateClass}">${tt.passRate}%</td>
            </tr>`;
        }).join('');

        const passedPct = summary.totalSteps > 0 ? Math.round((summary.totalPassed / summary.totalSteps) * 100) : 0;
        const failedPct = summary.totalSteps > 0 ? Math.round((summary.totalFailed / summary.totalSteps) * 100) : 0;
        const stoppedPct = Math.max(0, 100 - passedPct - failedPct);

        const configDetailsHtml = this.getReportConfigDetails(this.viewingReport());
        const configSectionHtml = configDetailsHtml.length > 0
            ? `<div class="config-section">
                <div class="config-title">REPORT CONFIGURATION</div>
                <div class="config-grid">${configDetailsHtml.map(d => `<div class="config-item"><span class="config-label">${d.label}</span><span class="config-value">${d.value}</span></div>`).join('')}</div>
            </div>`
            : '';

        const html = `<!DOCTYPE html>
<html lang="en"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1.0">
<title>Test Report - ${new Date().toLocaleDateString()}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Segoe UI',system-ui,sans-serif;background:#f8fafc;color:#1e293b;line-height:1.6;padding:40px}
.report{max-width:1000px;margin:0 auto;background:#fff;border-radius:12px;box-shadow:0 4px 20px rgba(0,0,0,0.06);overflow:hidden}
.header{border-bottom:3px solid #1a56db;padding:32px 40px}
.header-top{display:flex;align-items:center;gap:14px;margin-bottom:8px}
.logo{width:44px;height:44px;background:#1a56db;border-radius:10px;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:18px;flex-shrink:0}
h1{font-size:22px;font-weight:800}
.subtitle{font-size:13px;color:#64748b;margin-top:2px}
.meta{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-top:20px}
.meta-item .label{font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:.5px;font-weight:600}
.meta-item .value{font-size:13px;font-weight:600;margin-top:2px}
.body{padding:32px 40px}
.kpi-row{display:grid;grid-template-columns:repeat(5,1fr);gap:12px;margin-bottom:24px}
.kpi{padding:16px;border-radius:10px;text-align:center;border:1px solid #e2e8f0}
.kpi .num{font-size:28px;font-weight:800}
.kpi .lbl{font-size:10px;color:#64748b;text-transform:uppercase;letter-spacing:.5px;font-weight:600;margin-top:4px}
.kpi-total{background:#e8eefb;border-color:#c7d7f5}.kpi-total .num{color:#1a56db}
.kpi-pass{background:#d1fae5;border-color:#a7f3d0}.kpi-pass .num{color:#059669}
.kpi-fail{background:#fee2e2;border-color:#fecaca}.kpi-fail .num{color:#dc2626}
.kpi-stop{background:#f3f4f6;border-color:#e5e7eb}.kpi-stop .num{color:#6b7280}
.kpi-rate{background:#f0fdf4;border-color:#bbf7d0}.kpi-rate .num{color:#059669}
.progress-wrap{margin-bottom:28px}
.progress-bar{display:flex;height:12px;border-radius:6px;overflow:hidden;background:#f1f5f9}
.progress-bar .seg-pass{background:#059669}
.progress-bar .seg-fail{background:#dc2626}
.progress-bar .seg-stop{background:#d1d5db}
.progress-legend{display:flex;gap:20px;margin-top:8px;font-size:11px;color:#64748b}
.progress-legend .dot{width:8px;height:8px;border-radius:50%;display:inline-block;margin-right:4px;vertical-align:middle}
.section-title{display:flex;align-items:center;gap:10px;font-size:14px;font-weight:700;margin:24px 0 12px}
.section-num{width:26px;height:26px;background:#1a56db;border-radius:50%;color:#fff;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;flex-shrink:0}
table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:28px}
thead th{background:#f8fafc;padding:10px 12px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:#64748b;border-bottom:2px solid #e2e8f0}
tbody td{padding:10px 12px;border-bottom:1px solid #f1f5f9}
tbody tr:hover{background:#fafbfc}
.badge{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:12px;font-size:11px;font-weight:700}
.badge .dot{width:6px;height:6px;border-radius:50%;display:inline-block}
.badge-pass{background:#d1fae5;color:#059669}.badge-pass .dot{background:#059669}
.badge-fail{background:#fee2e2;color:#dc2626}.badge-fail .dot{background:#dc2626}
.badge-stop{background:#f3f4f6;color:#6b7280}.badge-stop .dot{background:#6b7280}
.td-pass{color:#059669;font-weight:600}
.td-fail{color:#dc2626;font-weight:600}
.td-rate-good{color:#059669;font-weight:700}
.td-rate-warn{color:#d97706;font-weight:700}
.td-rate-bad{color:#dc2626;font-weight:700}
.footer{margin-top:32px;padding-top:16px;border-top:2px solid #e2e8f0;display:flex;justify-content:space-between;font-size:11px;color:#94a3b8}
.config-section{background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:16px 20px;margin-bottom:24px}
.config-title{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:#94a3b8;margin-bottom:10px}
.config-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
.config-item{display:flex;flex-direction:column;gap:2px}
.config-label{font-size:10px;font-weight:600;text-transform:uppercase;color:#94a3b8}
.config-value{font-size:12px;font-weight:600;color:#1e293b}
@media print{body{padding:0;background:#fff}.report{box-shadow:none;border-radius:0}}
</style></head><body>
<div class="report">
<div class="header">
    <div class="header-top">
        <img src="${await this.getLogoBase64()}" alt="ActiveGenie" style="height:36px;width:auto" />
        <div><h1>Test Accelerator</h1></div>
    </div>
    <div class="meta">
        <div class="meta-item"><div class="label">Report Type</div><div class="value">${REPORT_TYPE_OPTIONS.find(o => o.id === this.selectedReportType())?.label || this.selectedReportType()}</div></div>
        <div class="meta-item"><div class="label">Total Scenarios</div><div class="value">${summary.totalScenarios}</div></div>
        <div class="meta-item"><div class="label">Pass Rate</div><div class="value">${summary.passRate}%</div></div>
        <div class="meta-item"><div class="label">Generated By</div><div class="value">${this.getCurrentUserName()}</div></div>
    </div>
</div>
<div class="body">
    ${configSectionHtml}
    <div class="section-title"><div class="section-num">1</div>Execution Overview</div>
    <div class="kpi-row">
        ${reportType === 'trend' ? `
        <div class="kpi kpi-total"><div class="num">${this.trendSummary().runsAnalyzed}</div><div class="lbl">Runs Analyzed</div></div>
        <div class="kpi kpi-pass"><div class="num">${this.trendSummary().avgPassRate}%</div><div class="lbl">Avg Pass Rate</div></div>
        <div class="kpi ${this.trendSummary().trendDirection >= 0 ? 'kpi-pass' : 'kpi-fail'}"><div class="num">${this.trendSummary().trendDirection >= 0 ? '+' : ''}${this.trendSummary().trendDirection}%</div><div class="lbl">Trend Direction</div></div>
        <div class="kpi kpi-stop"><div class="num">${this.trendSummary().flakyTests}</div><div class="lbl">Flaky Tests</div></div>
        <div class="kpi kpi-fail"><div class="num">${this.trendSummary().regressions}</div><div class="lbl">Regressions</div></div>
        ` : reportType === 'failure-analysis' ? `
        <div class="kpi kpi-fail"><div class="num">${this.failureAnalysisSummary().totalFailed}</div><div class="lbl">Total Failed</div></div>
        <div class="kpi kpi-stop"><div class="num">${this.failureAnalysisSummary().uniqueFailures}</div><div class="lbl">Unique Failures</div></div>
        <div class="kpi kpi-fail"><div class="num">${this.failureAnalysisSummary().recurring}</div><div class="lbl">Recurring (3+)</div></div>
        <div class="kpi kpi-total"><div class="num">${this.failureAnalysisSummary().mostImpactedArea}</div><div class="lbl">Most Impacted</div></div>
        <div class="kpi kpi-fail"><div class="num">${this.failureAnalysisSummary().avgFailureRate}%</div><div class="lbl">Avg Failure Rate</div></div>
        ` : `
        <div class="kpi kpi-total"><div class="num">${summary.totalScenarios}</div><div class="lbl">Total Scenarios</div></div>
        <div class="kpi kpi-pass"><div class="num">${summary.totalPassed}</div><div class="lbl">Passed</div></div>
        <div class="kpi kpi-fail"><div class="num">${summary.totalFailed}</div><div class="lbl">Failed</div></div>
        <div class="kpi kpi-stop"><div class="num">${summary.totalStopped}</div><div class="lbl">Stopped</div></div>
        <div class="kpi kpi-rate"><div class="num">${summary.passRate}%</div><div class="lbl">Pass Rate</div></div>
        `}
    </div>
    <div class="progress-wrap">
        <div class="progress-bar">
            <div class="seg-pass" style="width:${passedPct}%"></div>
            <div class="seg-fail" style="width:${failedPct}%"></div>
            <div class="seg-stop" style="width:${stoppedPct}%"></div>
        </div>
        <div class="progress-legend">
            <span><span class="dot" style="background:#059669"></span>Passed ${passedPct}%</span>
            <span><span class="dot" style="background:#dc2626"></span>Failed ${failedPct}%</span>
            <span><span class="dot" style="background:#d1d5db"></span>Stopped ${stoppedPct}%</span>
        </div>
    </div>
    ${(() => {
        const dl = this.selectedDetailLevel() || this.viewingReport()?.detailLevel || 'scenario';
        if (this.isComparisonMode()) return this.buildHtmlComparisonSection();
        let html = '';
        if (dl === 'scenario' || dl === 'step') {
            if (reportType === 'filtered' || reportType === 'execution-summary') {
                html += `<div class="section-title"><div class="section-num">2</div>Scenario Results</div>
                <table><thead><tr><th>#</th><th>Functional Area</th><th>Scenario</th><th>Package</th><th>Test Type</th><th>Steps</th><th>Passed</th><th>Failed</th><th>Status</th></tr></thead>
                <tbody>${scenarioRows}</tbody></table>`;
            } else if (reportType === 'trend') {
                html += `<div class="section-title"><div class="section-num">2</div>Scenario Trends</div>
                <table><thead><tr><th>#</th><th>Scenario</th><th>Functional Area</th><th>Pass Rate</th><th>Prev Pass Rate</th><th>Delta</th><th>Stability</th><th>Status</th></tr></thead>
                <tbody>${scenarioRows}</tbody></table>`;
            } else {
                html += `<div class="section-title"><div class="section-num">2</div>Failure Analysis</div>
                <table><thead><tr><th>#</th><th>Scenario</th><th>Functional Area</th><th>Failed Steps</th><th>Error Category</th><th>Recurrence</th><th>First Failed</th><th>Last Failed</th></tr></thead>
                <tbody>${scenarioRows}</tbody></table>`;
            }
        }
        if ((dl === 'functional-area' || dl === 'scenario' || dl === 'step') && faRows) {
            html += `<div class="section-title"><div class="section-num">3</div>Results by Functional Area</div>
            <table><thead><tr><th>Functional Area</th><th>Scenarios</th><th>Steps</th><th>Passed</th><th>Failed</th><th>Stopped</th><th>Pass Rate</th></tr></thead>
            <tbody>${faRows}</tbody></table>`;
        }
        if ((dl === 'test-type' || dl === 'scenario' || dl === 'step') && ttBreakdown.length > 0) {
            html += `<div class="section-title"><div class="section-num">4</div>Results by Test Type</div>
            <table><thead><tr><th>Test Type</th><th>Scenarios</th><th>Steps</th><th>Passed</th><th>Failed</th><th>Stopped</th><th>Pass Rate</th></tr></thead>
            <tbody>${ttRows}</tbody></table>`;
        }
        return html;
    })()}
    ${this.buildHtmlScreenshotSection(scenarios)}
    <div class="footer"><div>Test Accelerator &mdash; Confidential</div><div>Page 1 of 1</div><div>${new Date().toLocaleString()}</div></div>
</div></div></body></html>`;

        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const htmlReportType = this.selectedReportType() === 'filtered' ? 'custom-report' : this.selectedReportType();
        link.download = `${htmlReportType}-${new Date().toISOString().slice(0, 10)}.html`;
        link.click();
        URL.revokeObjectURL(url);
        this.messageService.success('HTML report downloaded');
    }

    // ===== Export Dispatcher =====

    async exportReport(): Promise<void> {
        const format = this.selectedExportFormat();
        if (!format) {
            this.messageService.warn('Please select an export format');
            return;
        }
        this.exportLoading.set(true);

        try {
            if (format === 'pdf') {
                await this.exportPdf();
            } else if (format === 'excel') {
                await this.exportExcel();
            } else if (format === 'html') {
                await this.exportHtml();
            } else if (format === 'pdf-excel' || format === 'all') {
                await this.exportPdf();
                await this.exportExcel();
                if (format === 'all') {
                    await this.exportHtml();
                }
            }
        } catch (err) {
            this.messageService.error('Export failed');
        } finally {
            this.exportLoading.set(false);
        }
    }

    // ===== Helpers =====

    getRowStatus(r: ReportRow): string {
        if (r.statusRef?.referenceValue) return r.statusRef.referenceValue;
        const passed = Number(r.passed) || 0;
        const failed = Number(r.failed) || 0;
        const total = r.numberOfSteps || 0;
        if (failed > 0) return 'Failed';
        if (passed === total && total > 0) return 'Passed';
        return 'Stopped';
    }

    private formatDuration(r: ReportRow): string {
        if (!r.executedOn) return 'N/A';
        return '—';
    }

    getReportTitle(): string {
        const titleMap: Record<ReportType, string> = {
            'execution-summary': 'Execution Summary Report',
            'filtered': 'Custom Test Report',
            'comparison': 'Execution Comparison Report',
            'trend': 'Trend Report',
            'failure-analysis': 'Failure Analysis Report'
        };
        const type = this.selectedReportType();
        return type ? titleMap[type] : 'Test Report';
    }

    getCurrentUserName(): string {
        const userInfo = this.authService.getUserInfoFromToken();
        if (!userInfo) return 'Unknown';
        return `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim() || 'Unknown';
    }

    getReportConfigDetails(report?: SavedReport | null): { label: string; value: string }[] {
        const cfg = report ? (report.config as any) : this.buildConfig();
        const reportType = report ? report.reportType : this.selectedReportType();
        const detailLevel = report ? report.detailLevel : this.selectedDetailLevel();
        if (!cfg) return [];
        const details: { label: string; value: string }[] = [];

        if (reportType) {
            details.push({ label: 'Report Type', value: this.getReportTypeLabel(reportType) });
        }

        const clientId = cfg.clientId;
        if (clientId) {
            const client = this.clientOptions().find(c => c.value === clientId);
            details.push({ label: 'Client', value: client?.label || (clientId === 'default' ? 'Master Client' : clientId) });
        }

        if (cfg.comparisonSelectType && reportType === 'comparison') {
            const opt = COMPARISON_SELECT_TYPE_OPTIONS.find(o => o.value === cfg.comparisonSelectType);
            details.push({ label: 'Select Type', value: opt?.label || cfg.comparisonSelectType });
        }

        if (cfg.comparisonSelectType === 'tenant-type') {
            if (cfg.baselineTenantId) {
                const tenant = this.tenantOptions().find(t => t.value === cfg.baselineTenantId);
                details.push({ label: 'Baseline Tenant', value: tenant?.label || cfg.baselineTenantId.substring(0, 8).toUpperCase() });
            }
            if (cfg.comparisonTenantId) {
                const tenant = this.tenantOptions().find(t => t.value === cfg.comparisonTenantId);
                details.push({ label: 'Compare With Tenant', value: tenant?.label || cfg.comparisonTenantId.substring(0, 8).toUpperCase() });
            }
        } else {
            if (cfg.executionRunId) {
                const run = this.executionRuns().find(r => r.id === cfg.executionRunId);
                details.push({
                    label: reportType === 'comparison' ? 'Baseline Run' : 'Execution Run',
                    value: run ? this.formatRunLabel(run) : cfg.executionRunId.substring(0, 8).toUpperCase()
                });
            }

            if (cfg.comparisonRunId) {
                const run = this.executionRuns().find(r => r.id === cfg.comparisonRunId);
                details.push({
                    label: 'Compare With',
                    value: run ? this.formatRunLabel(run) : cfg.comparisonRunId.substring(0, 8).toUpperCase()
                });
            }
        }

        if (cfg.packageId) {
            const pkg = this.packageOptions().find(p => p.value === cfg.packageId);
            details.push({ label: 'Package', value: pkg?.label || cfg.packageId });
        }

        if (cfg.testTypeId) {
            const tt = this.testTypeOptions().find(t => t.value === cfg.testTypeId);
            details.push({ label: 'Test Type', value: tt?.label || cfg.testTypeId });
        }

        if (cfg.functionalAreaIds?.length > 0) {
            const names = cfg.functionalAreaIds.map((id: string) => {
                const fa = this.functionalAreaOptions().find(f => f.value === id);
                return fa?.label || id;
            });
            details.push({ label: 'Functional Areas', value: names.join(', ') });
        }

        if (cfg.statusFilter) {
            details.push({ label: 'Status Filter', value: cfg.statusFilter });
        }

        const mode = cfg.dateRangeMode || 'fixed';
        if (mode !== 'fixed') {
            const modeOpt = this.dateRangeModeOptions.find(m => m.value === mode);
            details.push({ label: 'Date Range', value: modeOpt?.label || mode });
        } else if (cfg.dateRange?.length === 2) {
            const from = new Date(cfg.dateRange[0]).toLocaleDateString();
            const to = new Date(cfg.dateRange[1]).toLocaleDateString();
            details.push({ label: 'Date Range', value: `${from} — ${to}` });
        }

        if (detailLevel) {
            const dl = DETAIL_LEVEL_OPTIONS.find(d => d.value === detailLevel);
            details.push({ label: 'Detail Level', value: dl?.label || detailLevel });
        }

        return details;
    }

    private buildEditSnapshot(): object {
        return {
            reportName: this.saveReportName(),
            reportType: this.selectedReportType(),
            clientId: this.selectedClient(),
            executionRunId: this.selectedExecutionRunId(),
            comparisonRunId: this.selectedComparisonRunId(),
            packageId: this.selectedPackage(),
            testTypeId: this.selectedTestType(),
            functionalAreaIds: this.selectedFunctionalAreas(),
            statusFilter: this.selectedStatus(),
            dateRangeMode: this.selectedDateRangeMode(),
            detailLevel: this.selectedDetailLevel(),
            exportFormat: this.selectedExportFormat(),
            includeScreenshots: this.includeScreenshots(),
            includeErrorLogs: this.includeErrorLogs(),
            includeStepDurations: this.includeStepDurations()
        };
    }

    getGeneratedByName(report: SavedReport): string {
        if (report.generatedByUser) {
            return `${report.generatedByUser.firstName || ''} ${report.generatedByUser.lastName || ''}`.trim() || 'Unknown';
        }
        return 'Unknown';
    }

    getClientNameForReport(report: SavedReport): string {
        const clientId = report.config?.clientId;
        if (!clientId) return '—';
        if (clientId === 'default') return 'Default';
        const client = this.clientOptions().find(c => c.value === clientId);
        return client?.label || '—';
    }

    getConfigSummaryForReport(report: SavedReport): string {
        const cfg = report.config;
        if (!cfg) return '—';
        const parts: string[] = [];
        if (cfg.executionRunId) {
            const run = this.executionRuns().find(r => r.id === cfg.executionRunId);
            if (run) {
                parts.push(`Run: ${run.runId}`);
            }
        }
        if (cfg.packageId) {
            const pkgName = this.allPackageMap().get(cfg.packageId);
            if (pkgName) {
                parts.push(`Pkg: ${pkgName}`);
            }
        }
        if (cfg.testTypeId) {
            const ttName = this.allTestTypeMap().get(cfg.testTypeId);
            if (ttName) {
                parts.push(`Type: ${ttName}`);
            }
        }
        if (cfg.functionalAreaIds && cfg.functionalAreaIds.length > 0) {
            const faMap = this.allFunctionalAreaMap();
            const names = cfg.functionalAreaIds.map(id => faMap.get(id) || null).filter(Boolean);
            if (names.length > 0) {
                parts.push(`FA: ${names.join(', ')}`);
            }
        }
        if (cfg.statusFilter && cfg.statusFilter !== 'all') {
            parts.push(`Status: ${cfg.statusFilter}`);
        }
        if (report.detailLevel) {
            const dl = this.detailLevelOptions.find(o => o.value === report.detailLevel);
            parts.push(dl?.label || report.detailLevel);
        }
        if (report.exportFormat) {
            parts.push(report.exportFormat.toUpperCase());
        }
        return parts.length > 0 ? parts.join(' · ') : '—';
    }

    getStatusSeverity(status: string): 'success' | 'danger' | 'secondary' {
        if (status === 'Passed') return 'success';
        if (status === 'Failed') return 'danger';
        return 'secondary';
    }

    getChangeTagSeverity(changeType: string): 'success' | 'danger' | 'warn' | 'secondary' {
        if (changeType === 'fixed') return 'success';
        if (changeType === 'regression') return 'danger';
        if (changeType === 'new-fail') return 'warn';
        return 'secondary';
    }

    getChangeTagLabel(changeType: string): string {
        const map: Record<string, string> = {
            'fixed': 'Fixed',
            'regression': 'Regression',
            'new-fail': 'New Fail',
            'stable-pass': 'Stable',
            'stable-fail': 'Stable Fail'
        };
        return map[changeType] || changeType;
    }

    getRunLabel(runId: string | null): string {
        if (!runId) return 'N/A';
        const run = this.executionRuns().find(r => r.id === runId);
        if (!run) return runId.substring(0, 8).toUpperCase();
        const date = new Date(run.executedOn).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        return `${run.runId.substring(0, 8).toUpperCase()} | ${date} | ${run.packageName}`;
    }

    getBaselineRunLabel(): string {
        const cd = (this.viewingReport()?.config as any)?.comparisonData;
        if (cd?.baselineRunLabel) return cd.baselineRunLabel;
        if (this.isTenantComparisonMode()) {
            return this.getTenantLabel(this.selectedBaselineTenantId());
        }
        return this.getRunLabel(this.selectedExecutionRunId());
    }

    getComparisonRunLabel(): string {
        const cd = (this.viewingReport()?.config as any)?.comparisonData;
        if (cd?.currentRunLabel) return cd.currentRunLabel;
        if (this.isTenantComparisonMode()) {
            return this.getTenantLabel(this.selectedComparisonTenantId());
        }
        return this.getRunLabel(this.selectedComparisonRunId());
    }

    private getTenantLabel(tenantId: string | null): string {
        if (!tenantId) return 'N/A';
        const tenant = this.tenantOptions().find(t => t.value === tenantId);
        return tenant?.label || tenantId.substring(0, 8).toUpperCase();
    }

    getBaselinePassedCount(): number {
        return this.baselineScenarios().filter(s => s.status === 'Passed').length;
    }

    getBaselineFailedCount(): number {
        return this.baselineScenarios().filter(s => s.status === 'Failed').length;
    }

    getCurrentPassedCount(): number {
        return this.currentScenarios().filter(s => s.status === 'Passed').length;
    }

    getCurrentFailedCount(): number {
        return this.currentScenarios().filter(s => s.status === 'Failed').length;
    }

    getReportTypeLabel(type?: ReportType): string {
        const t = type || this.selectedReportType();
        if (!t) return '';
        const opt = REPORT_TYPE_OPTIONS.find(o => o.id === t);
        return opt?.label || t;
    }

    getSelectedClientLabel(): string {
        const clientId = this.selectedClient();
        if (!clientId) return '';
        const opt = this.clientOptions().find(o => o.value === clientId);
        return opt?.label || '';
    }

    getSelectedRunLabel(): string {
        const runId = this.selectedExecutionRunId();
        if (!runId) return '';
        const run = this.filteredExecutionRuns().find(r => r.id === runId);
        return run?.runId || '';
    }

    getDetailLevelLabel(): string {
        const val = this.selectedDetailLevel();
        if (!val) return '';
        const opt = DETAIL_LEVEL_OPTIONS.find(o => o.value === val);
        return opt?.label || val;
    }

    getExportFormatLabel(): string {
        const val = this.selectedExportFormat();
        if (!val) return '';
        const opt = EXPORT_FORMAT_OPTIONS.find(o => o.value === val);
        return opt?.label || val;
    }

    getReportTypeIcon(type: ReportType): string {
        const opt = REPORT_TYPE_OPTIONS.find(o => o.id === type);
        return opt?.icon || 'pi pi-file';
    }

    getReportTypeColor(type: ReportType): string {
        const colorMap: Record<ReportType, string> = {
            'execution-summary': '#1a56db',
            'filtered': '#7c3aed',
            'comparison': '#d97706',
            'trend': '#059669',
            'failure-analysis': '#dc2626'
        };
        return colorMap[type] || '#64748b';
    }

    getReportTypeSeverity(type: ReportType): 'info' | 'success' | 'warn' | 'danger' | 'secondary' | 'contrast' {
        const severityMap: Record<ReportType, 'info' | 'success' | 'warn' | 'danger' | 'secondary' | 'contrast'> = {
            'execution-summary': 'info',
            'filtered': 'secondary',
            'comparison': 'warn',
            'trend': 'success',
            'failure-analysis': 'danger'
        };
        return severityMap[type] || 'info';
    }

    // ===== Saved Reports =====

    loadSavedReports(): void {
        this.savedReportsLoading.set(true);
        this.reportGeneratorApiService.getSavedReports().subscribe({
            next: (reports) => {
                this.savedReports.set(reports || []);
                this.savedReportsLoading.set(false);
            },
            error: () => {
                this.savedReports.set([]);
                this.savedReportsLoading.set(false);
            }
        });
    }

    openSaveDialog(): void {
        if (!this.editingReportId()) {
            const defaultName = `${this.getReportTitle()} - ${new Date().toLocaleDateString()}`;
            this.saveReportName.set(defaultName);
        }
        this.showSaveDialog.set(true);
    }

    confirmSaveReport(): void {
        const name = this.saveReportName().trim();
        if (!name) {
            this.messageService.error('Please enter a report name');
            return;
        }

        const summary = this.previewSummary();
        const saved: Partial<SavedReport> = {
            reportName: name,
            reportType: this.selectedReportType()!,
            detailLevel: this.selectedDetailLevel(),
            exportFormat: this.selectedExportFormat(),
            generatedOn: new Date().toISOString(),
            passRate: summary.passRate,
            totalScenarios: summary.totalScenarios,
            totalPassed: summary.totalPassed,
            totalFailed: summary.totalFailed,
            config: {
                ...this.buildConfig(),
                ...(this.isComparisonMode() ? {
                    comparisonData: {
                        deltas: this.comparisonDeltas(),
                        summary: this.comparisonSummary(),
                        baselineScenarios: this.baselineScenarios(),
                        currentScenarios: this.currentScenarios(),
                        baselineRunLabel: this.getBaselineRunLabel(),
                        currentRunLabel: this.getComparisonRunLabel()
                    }
                } : {})
            },
            previewSummary: summary,
            previewScenarios: this.previewScenarios(),
            functionalAreaBreakdown: this.previewFunctionalAreaBreakdown(),
            testTypeBreakdown: this.previewTestTypeBreakdown(),
            ...(this.selectedReportType() === 'trend' ? { trendSummaryData: this.trendSummary() } : {}),
            ...(this.selectedReportType() === 'failure-analysis' ? { failureAnalysisSummaryData: this.failureAnalysisSummary() } : {})
        };

        const editId = this.editingReportId();
        const request$ = editId
            ? this.reportGeneratorApiService.updateSavedReport(editId, saved)
            : this.reportGeneratorApiService.saveReport(saved);

        request$.subscribe({
            next: (result) => {
                if (editId) {
                    this.savedReports.update(list =>
                        list.map(r => r.id === editId ? result : r)
                    );
                } else {
                    this.savedReports.update(list => [result, ...list]);
                }
                this.showSaveDialog.set(false);
                this.editingReportId.set(null);
                this.messageService.success(editId ? 'Report updated successfully' : 'Report saved successfully');
                this.activeTab.set('list');
            },
            error: () => {
                this.messageService.error(editId ? 'Failed to update report' : 'Failed to save report');
            }
        });
    }

    viewSavedReport(report: SavedReport): void {
        this.viewingReport.set(report);
        this.selectedReportType.set(report.reportType);
        this.selectedDetailLevel.set(report.detailLevel);
        this.selectedExportFormat.set(report.exportFormat);

        const cfg = report.config as any;
        if (cfg?.includeScreenshots !== undefined) this.includeScreenshots.set(cfg.includeScreenshots);
        if (cfg?.includeErrorLogs !== undefined) this.includeErrorLogs.set(cfg.includeErrorLogs);

        this.selectedClient.set(cfg?.clientId || null);
        this.selectedExecutionRunId.set(cfg?.executionRunId || null);
        this.selectedPackage.set(cfg?.packageId || null);
        this.selectedTestType.set(cfg?.testTypeId || null);
        this.selectedFunctionalAreas.set(cfg?.functionalAreaIds || []);
        this.selectedStatus.set(cfg?.statusFilter || null);

        const mode: DateRangeMode = cfg?.dateRangeMode || 'fixed';
        this.selectedDateRangeMode.set(mode);
        if (mode !== 'fixed') {
            this.dateRange.set(this.resolveDynamicDateRange(mode));
        } else if (cfg?.dateRange) {
            this.dateRange.set(cfg.dateRange.map((d: string) => new Date(d)));
        } else {
            this.dateRange.set(null);
        }

        if (report.reportType === 'comparison') {
            this.loadComparisonViewData(report, cfg);
            this.showPreview.set(true);
            return;
        }

        this.refreshViewWithLiveData(report);
    }

    private refreshViewWithLiveData(report: SavedReport): void {
        this.isGenerating.set(true);
        this.summaryLoading.set(true);
        this.previewLoading.set(true);
        this.cachedReports.set([]);
        this.streamProgress.set({ loaded: 0, total: 0 });
        this.streamComplete.set(false);
        this.showPreview.set(true);

        const startStream = () => {
            const filters = this.buildStreamFilters();

            this.reportGeneratorApiService.getFilteredSummary(filters).subscribe({
                next: (res: any) => {
                    const data = res?.data || res;
                    this.previewSummary.set({
                        totalScenarios: data.totalScenarios || 0,
                        totalSteps: data.totalSteps || 0,
                        totalPassed: data.totalPassed || 0,
                        totalFailed: data.totalFailed || 0,
                        totalStopped: data.totalStopped || 0,
                        passRate: data.passRate || 0
                    });
                    this.summaryLoading.set(false);
                },
                error: () => {
                    this.summaryLoading.set(false);
                    this.previewSummary.set(report.previewSummary || { totalScenarios: 0, totalSteps: 0, totalPassed: 0, totalFailed: 0, totalStopped: 0, passRate: 0 });
                }
            });

            if (this.streamSubscription) {
                this.streamSubscription.unsubscribe();
            }

            this.streamSubscription = this.reportGeneratorApiService.streamReports(filters).subscribe({
                next: (line: string) => {
                    try {
                        const batch = JSON.parse(line);
                        const currentCache = this.cachedReports();
                        const newRows = [...currentCache, ...(batch.rows || [])];
                        this.cachedReports.set(newRows);
                        this.streamProgress.set({ loaded: batch.loaded || newRows.length, total: batch.total || 0 });

                        if (this.previewLoading()) {
                            this.previewLoading.set(false);
                            this.buildPreviewFromCache();
                            this.isGenerating.set(false);
                        }

                        if (batch.done) {
                            this.streamComplete.set(true);
                            this.buildPreviewFromCache();
                        }
                    } catch (e) {
                        console.error('Failed to parse stream batch:', e);
                    }
                },
                error: () => {
                    this.previewLoading.set(false);
                    this.isGenerating.set(false);
                    this.previewScenarios.set(report.previewScenarios || []);
                    this.previewFunctionalAreaBreakdown.set(report.functionalAreaBreakdown || []);
                    this.previewTestTypeBreakdown.set(report.testTypeBreakdown || []);
                    if (report.reportType === 'trend') {
                        const saved = (report as any).trendSummaryData;
                        if (saved) this.trendSummary.set(saved);
                        else this.recomputeTrendSummaryFromScenarios(report.previewScenarios || []);
                    }
                    if (report.reportType === 'failure-analysis') {
                        const saved = (report as any).failureAnalysisSummaryData;
                        if (saved) this.failureAnalysisSummary.set(saved);
                        else this.recomputeFailureAnalysisSummaryFromScenarios(report.previewScenarios || []);
                    }
                },
                complete: () => {
                    this.streamComplete.set(true);
                }
            });
        };

        if (this.allReports().length > 0) {
            startStream();
        } else {
            this.loadReports(() => startStream());
        }
    }

    private loadComparisonViewData(report: SavedReport, cfg: any): void {
        if (cfg?.comparisonSelectType) this.selectedComparisonSelectType.set(cfg.comparisonSelectType);
        if (cfg?.baselineTenantId) this.selectedBaselineTenantId.set(cfg.baselineTenantId);
        if (cfg?.comparisonTenantId) this.selectedComparisonTenantId.set(cfg.comparisonTenantId);
        if (cfg?.executionRunId) this.selectedExecutionRunId.set(cfg.executionRunId);
        if (cfg?.comparisonRunId) this.selectedComparisonRunId.set(cfg.comparisonRunId);

        this.previewSummary.set(report.previewSummary || { totalScenarios: 0, totalSteps: 0, totalPassed: 0, totalFailed: 0, totalStopped: 0, passRate: 0 });
        this.previewScenarios.set(report.previewScenarios || []);
        this.previewFunctionalAreaBreakdown.set(report.functionalAreaBreakdown || []);
        this.previewTestTypeBreakdown.set(report.testTypeBreakdown || []);

        const cd = cfg?.comparisonData;
        if (cd?.deltas && cd.deltas.length > 0) {
            this.comparisonDeltas.set(cd.deltas);
            this.comparisonSummary.set(cd.summary || { beforePassRate: 0, afterPassRate: 0, passRateDelta: 0, nowPassing: 0, regressions: 0, newFailures: 0 });
            this.baselineScenarios.set(cd.baselineScenarios || []);
            this.currentScenarios.set(cd.currentScenarios || []);
        } else {
            this.rebuildComparisonFromScenarios(report);
        }
    }

    editSavedReport(report: SavedReport): void {
        ReportGeneratorViewModel.pendingEditReport = report;
        this.activeTab.set('generate');
    }

    applyPendingEdit(): void {
        const report = ReportGeneratorViewModel.pendingEditReport;
        if (!report) return;
        ReportGeneratorViewModel.pendingEditReport = null;

        this.selectedReportType.set(report.reportType);

        const cfg = report.config as any;
        if (cfg) {
            if (cfg.clientId) {
                this.selectedClient.set(cfg.clientId);
                this.loadFunctionalAreasForClient(cfg.clientId);
            }
            if (cfg.comparisonSelectType) this.selectedComparisonSelectType.set(cfg.comparisonSelectType);
            if (cfg.baselineTenantId) this.selectedBaselineTenantId.set(cfg.baselineTenantId);
            if (cfg.comparisonTenantId) this.selectedComparisonTenantId.set(cfg.comparisonTenantId);
            if (cfg.executionRunId) this.selectedExecutionRunId.set(cfg.executionRunId);
            if (cfg.comparisonRunId) this.selectedComparisonRunId.set(cfg.comparisonRunId);
            if (cfg.packageId) this.selectedPackage.set(cfg.packageId);
            if (cfg.testTypeId) this.selectedTestType.set(cfg.testTypeId);
            if (cfg.functionalAreaIds) this.selectedFunctionalAreas.set(cfg.functionalAreaIds);
            if (cfg.statusFilter) this.selectedStatus.set(cfg.statusFilter);
            if (cfg.dateRangeMode) {
                this.selectedDateRangeMode.set(cfg.dateRangeMode);
                if (cfg.dateRangeMode !== 'fixed') {
                    this.dateRange.set(this.resolveDynamicDateRange(cfg.dateRangeMode));
                } else if (cfg.dateRange) {
                    this.dateRange.set(cfg.dateRange.map((d: string) => new Date(d)));
                }
            } else if (cfg.dateRange) {
                this.dateRange.set(cfg.dateRange.map((d: string) => new Date(d)));
            }
            if (cfg.includeScreenshots !== undefined) this.includeScreenshots.set(cfg.includeScreenshots);
            if (cfg.includeErrorLogs !== undefined) this.includeErrorLogs.set(cfg.includeErrorLogs);
            if (cfg.includeStepDurations !== undefined) this.includeStepDurations.set(cfg.includeStepDurations);
        }

        this.selectedDetailLevel.set(report.detailLevel as any);
        this.selectedExportFormat.set(report.exportFormat as any);
        this.editingReportId.set(report.id);
        this.saveReportName.set(report.reportName);
        this.activeStep.set(1);

        this.originalEditSnapshot.set(JSON.stringify(this.buildEditSnapshot()));
    }

    closeViewReport(): void {
        this.viewingReport.set(null);
        this.showPreview.set(false);
    }

    openRenameDialog(report: SavedReport): void {
        this.renameReportId.set(report.id);
        this.renameReportName.set(report.reportName);
        this.showRenameDialog.set(true);
    }

    confirmRenameReport(): void {
        const id = this.renameReportId();
        const name = this.renameReportName().trim();
        if (!name) {
            this.messageService.error('Please enter a report name');
            return;
        }

        this.reportGeneratorApiService.updateSavedReport(id, { reportName: name }).subscribe({
            next: () => {
                this.savedReports.update(list =>
                    list.map(r => r.id === id ? { ...r, reportName: name } : r)
                );
                this.showRenameDialog.set(false);
                this.messageService.success('Report renamed');
            },
            error: () => {
                this.messageService.error('Failed to rename report');
            }
        });
    }

    deleteSavedReport(report: SavedReport): void {
        this.confirmationService.confirm({
            message: `Delete "${report.reportName}"? This action cannot be undone.`,
            header: 'Delete Report',
            icon: 'pi pi-trash',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => {
                this.reportGeneratorApiService.deleteSavedReport(report.id).subscribe({
                    next: () => {
                        this.savedReports.update(list => list.filter(r => r.id !== report.id));
                        this.messageService.success('Report deleted');
                    },
                    error: () => {
                        this.messageService.error('Failed to delete report');
                    }
                });
            }
        });
    }

    async exportSavedReport(report: SavedReport, format: ExportFormat): Promise<void> {
        this.selectedReportType.set(report.reportType);
        this.selectedDetailLevel.set(report.detailLevel);

        const cfg = report.config as any;
        const mode: DateRangeMode = cfg?.dateRangeMode || 'fixed';

        if (mode !== 'fixed' && report.reportType !== 'comparison') {
            this.selectedDateRangeMode.set(mode);
            this.dateRange.set(this.resolveDynamicDateRange(mode));
            if (cfg?.clientId) this.selectedClient.set(cfg.clientId);
            if (cfg?.executionRunId) this.selectedExecutionRunId.set(cfg.executionRunId);
            if (cfg?.packageId) this.selectedPackage.set(cfg.packageId);
            if (cfg?.testTypeId) this.selectedTestType.set(cfg.testTypeId);
            if (cfg?.functionalAreaIds) this.selectedFunctionalAreas.set(cfg.functionalAreaIds);
            if (cfg?.statusFilter) this.selectedStatus.set(cfg.statusFilter);

            const reports = this.getFilteredReports();
            this.buildStandardPreview(reports);
        } else {
            this.previewSummary.set(report.previewSummary || { totalScenarios: 0, totalSteps: 0, totalPassed: 0, totalFailed: 0, totalStopped: 0, passRate: 0 });
            this.previewScenarios.set(report.previewScenarios || []);
            this.previewFunctionalAreaBreakdown.set(report.functionalAreaBreakdown || []);
            this.previewTestTypeBreakdown.set(report.testTypeBreakdown || []);
        }

        if (report.reportType === 'comparison') {
            if (cfg?.comparisonSelectType) this.selectedComparisonSelectType.set(cfg.comparisonSelectType);
            if (cfg?.baselineTenantId) this.selectedBaselineTenantId.set(cfg.baselineTenantId);
            if (cfg?.comparisonTenantId) this.selectedComparisonTenantId.set(cfg.comparisonTenantId);
            if (cfg?.executionRunId) this.selectedExecutionRunId.set(cfg.executionRunId);
            if (cfg?.comparisonRunId) this.selectedComparisonRunId.set(cfg.comparisonRunId);

            const cd = cfg?.comparisonData;
            if (cd?.deltas && cd.deltas.length > 0) {
                this.comparisonDeltas.set(cd.deltas);
                this.comparisonSummary.set(cd.summary || { beforePassRate: 0, afterPassRate: 0, passRateDelta: 0, nowPassing: 0, regressions: 0, newFailures: 0 });
                this.baselineScenarios.set(cd.baselineScenarios || []);
                this.currentScenarios.set(cd.currentScenarios || []);
            } else {
                this.rebuildComparisonFromScenarios(report);
            }
        }

        if (format === 'pdf') await this.exportPdf();
        else if (format === 'excel') await this.exportExcel();
        else if (format === 'html') await this.exportHtml();
    }

    shareSavedReport(report: SavedReport): void {
        const shareUrl = `${window.location.origin}/report-generator?view=${report.id}`;
        this.shareLink.set(shareUrl);
        this.showShareDialog.set(true);
    }

    copyShareLink(): void {
        navigator.clipboard.writeText(this.shareLink()).then(() => {
            this.messageService.success('Link copied to clipboard');
        }).catch(() => {
            this.messageService.error('Failed to copy link');
        });
    }

    getTimeSince(dateStr: string): string {
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        const diffHrs = Math.floor(diffMins / 60);
        if (diffHrs < 24) return `${diffHrs}h ago`;
        const diffDays = Math.floor(diffHrs / 24);
        if (diffDays < 30) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    }
}
